import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Dropdown, Form, Modal, OverlayTrigger, Row, Spinner, Table, Tooltip } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";

import { send as sendAlert } from "../../../actions/alert";
import { send as pageInfo } from "../../../actions/page";
import CsLineIcons from "../../../cs-line-icons";
import ConfirmModal from "../../../layout/modal";
import signApi from "../../../utils/api/signature";
import { apiErrorMessage, copySigningLink, signingLinksFromResponse } from "../../../utils/signature-links";
import { SIGNATURE_METADATA_FIELDS_ENABLED, SIGNATURE_TEMPLATE_CONVERSION_ENABLED } from "../features";

const FIELD_TYPES = [
    { value: "signature", label: "Signature" },
    { value: "name", label: "Full Name" },
    { value: "date_signed", label: "Date" },
    { value: "initials", label: "Initials" },
    { value: "email", label: "Email Address" },
    { value: "text", label: "Text Field" },
    { value: "checkbox", label: "Checkbox" },
    { value: "radio", label: "Radio" },
    { value: "dropdown", label: "Dropdown" }
];

const STANDARD_FIELDS = [
    { value: "signature", label: "Signature", width: 0.32, height: 0.07 },
    { value: "name", label: "Full Name", width: 0.26, height: 0.045 },
    { value: "date_signed", label: "Date", width: 0.16, height: 0.04 },
    { value: "initials", label: "Initials", width: 0.12, height: 0.045 },
    { value: "email", label: "Email Address", width: 0.28, height: 0.04 },
    { value: "text", label: "Text Field", width: 0.28, height: 0.05 }
];

const FIELD_ALIGNMENTS = [
    { value: "left", label: "Left", icon: "align-left" },
    { value: "center", label: "Center", icon: "align-center" },
    { value: "right", label: "Right", icon: "align-right" }
];

const MULTI_FIELD_VERTICAL_ALIGNMENTS = [
    { value: "top", label: "Top", icon: "arrow-top" },
    { value: "middle", label: "Center", icon: "align-center", iconStyle: { display: "inline-block", transform: "rotate(90deg)" } },
    { value: "bottom", label: "Bottom", icon: "arrow-bottom" }
];

const FIELD_FONTS = [
    { value: "helvetica", label: "Helvetica", css: "Arial, Helvetica, sans-serif" },
    { value: "times", label: "Times", css: "\"Times New Roman\", Times, serif" },
    { value: "courier", label: "Courier", css: "\"Courier New\", Courier, monospace" }
];

const FIELD_FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24];

const FIELD_FONT_SIZE_OPTIONS = FIELD_FONT_SIZES.map((size) => ({
    value: size,
    label: String(size)
}));

const REMINDER_FREQUENCY_OPTIONS = [
    { value: "daily", label: "Daily" },
    { value: "none", label: "None" }
];

const DEFAULT_FIELD_METADATA = {
    textAlign: "left",
    fontFamily: "helvetica",
    fontSize: 12
};

const SIGNER_COLORS = [
    { border: "#0d6efd", background: "rgba(13, 110, 253, 0.16)", selected: "rgba(13, 110, 253, 0.26)", text: "#084298" },
    { border: "#198754", background: "rgba(25, 135, 84, 0.16)", selected: "rgba(25, 135, 84, 0.26)", text: "#0f5132" },
    { border: "#fd7e14", background: "rgba(253, 126, 20, 0.18)", selected: "rgba(253, 126, 20, 0.30)", text: "#984c0c" },
    { border: "#6f42c1", background: "rgba(111, 66, 193, 0.16)", selected: "rgba(111, 66, 193, 0.26)", text: "#432874" },
    { border: "#0dcaf0", background: "rgba(13, 202, 240, 0.18)", selected: "rgba(13, 202, 240, 0.30)", text: "#055160" },
    { border: "#d63384", background: "rgba(214, 51, 132, 0.16)", selected: "rgba(214, 51, 132, 0.26)", text: "#801f4f" }
];

const DEFAULT_FIELD_COLOR = {
    border: "#6c757d",
    background: "rgba(108, 117, 125, 0.14)",
    selected: "rgba(108, 117, 125, 0.24)",
    text: "#343a40"
};

const RECIPIENT_MENU_POPPER = { strategy: "fixed" };
const REACT_SELECT_MENU_STYLES = {
    menuPortal: (base) => ({ ...base, zIndex: 1080 })
};
const MIN_FIELD_SIZE = 0.02;
const FIELD_GRID_STEP = 0.01;
const FIELD_NUDGE_STEP = 0.005;
const FIELD_NUDGE_FAST_STEP = 0.025;
const FIELD_RESIZE_KEY_STEP = 0.005;
const FIELD_HISTORY_LIMIT = 40;
const API_FIELD_NAME_PATTERN = /^[a-z0-9][a-z0-9._-]{0,127}$/;
const PDF_MIN_ZOOM = 0.6;
const PDF_MAX_ZOOM = 1.6;
const PDF_ZOOM_STEP = 0.15;
const FIELD_NUDGE_DELTAS = {
    ArrowLeft: { x: -1, y: 0 },
    ArrowRight: { x: 1, y: 0 },
    ArrowUp: { x: 0, y: -1 },
    ArrowDown: { x: 0, y: 1 }
};
const FIELD_RESIZE_HANDLES = [
    { key: "nw", cursor: "nwse-resize", style: { left: -6, top: -6 } },
    { key: "n", cursor: "ns-resize", style: { left: "50%", top: -6, transform: "translateX(-50%)" } },
    { key: "ne", cursor: "nesw-resize", style: { right: -6, top: -6 } },
    { key: "e", cursor: "ew-resize", style: { right: -6, top: "50%", transform: "translateY(-50%)" } },
    { key: "se", cursor: "nwse-resize", style: { right: -6, bottom: -6 } },
    { key: "s", cursor: "ns-resize", style: { left: "50%", bottom: -6, transform: "translateX(-50%)" } },
    { key: "sw", cursor: "nesw-resize", style: { left: -6, bottom: -6 } },
    { key: "w", cursor: "ew-resize", style: { left: -6, top: "50%", transform: "translateY(-50%)" } }
];

const BUILDER_STEPS = [
    { value: "signers", label: "Signing Recipients" },
    { value: "fields", label: "Fields" },
    { value: "final", label: "Delivery" },
    { value: "activity", label: "Activity" }
];

const DEFAULT_FIELD = {
    recipientUuid: "",
    documentUuid: "",
    fieldType: "signature",
    pageNumber: 1,
    x: 0.55,
    y: 0.72,
    width: 0.35,
    height: 0.08,
    required: true,
    label: "",
    metadata: DEFAULT_FIELD_METADATA
};

const EMPTY_SELECTED_FIELD = {
    recipientUuid: "",
    fieldType: "signature",
    pageNumber: 1,
    x: 0.55,
    y: 0.72,
    width: 0.32,
    height: 0.07,
    required: true,
    label: "",
    apiName: "",
    validation: "",
    textAlign: DEFAULT_FIELD_METADATA.textAlign,
    fontFamily: DEFAULT_FIELD_METADATA.fontFamily,
    fontSize: DEFAULT_FIELD_METADATA.fontSize
};

const SELECTED_FIELD_FORM_FIELDS = [
    "recipientUuid",
    "fieldType",
    "pageNumber",
    "x",
    "y",
    "width",
    "height",
    "required",
    "label",
    "apiName",
    "validation",
    "textAlign",
    "fontFamily",
    "fontSize"
];

const MULTI_SELECTED_FIELD_SYNC_FIELDS = new Set([
    "x",
    "y",
    "width",
    "height",
    "required",
    "textAlign",
    "fontFamily",
    "fontSize"
]);

const SELECTED_FIELD_GEOMETRY_FIELDS = new Set(["x", "y", "width", "height"]);
const SELECTED_FIELD_METADATA_FIELDS = new Set(["textAlign", "fontFamily", "fontSize"]);

function dateInputValue(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
}

function addDays(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 10);
}

function documentTitle(document) {
    return document?.displayName || document?.originalFileName || "Document";
}

function objectMetadata(source) {
    const metadata = source?.metadata;
    if (!metadata) return {};
    if (typeof metadata === "object" && !Array.isArray(metadata)) return metadata;
    if (typeof metadata !== "string") return {};

    try {
        const parsed = JSON.parse(metadata);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

function documentMetadata(document) {
    return objectMetadata(document);
}

function fieldValidationText(validation) {
    if (!validation) return "";
    if (typeof validation === "string") return validation;

    try {
        return JSON.stringify(validation, null, 2);
    } catch {
        return "";
    }
}

function fieldValidationPayload(value) {
    const text = String(value || "").trim();
    if (!text) return null;

    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

function normalizeTagTerm(value) {
    return String(value || "").trim().replace(/\s+/g, " ");
}

function tagTerms(value) {
    const source = Array.isArray(value)
        ? value
        : String(value || "").split(",");
    const seen = new Set();
    const terms = [];
    source
        .map(normalizeTagTerm)
        .filter(Boolean)
        .forEach((term) => {
            const key = term.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            terms.push(term);
        });
    return terms;
}

function tagTermsText(terms) {
    return tagTerms(terms).join(", ");
}

function envelopeIsTemplate(envelope) {
    const metadata = objectMetadata(envelope);
    const template = metadata.template && typeof metadata.template === "object" && !Array.isArray(metadata.template)
        ? metadata.template
        : {};
    return metadata.isTemplate === true || template.isTemplate === true || metadata.isTemplate === "true" || template.isTemplate === "true";
}

function documentConversion(document) {
    const metadata = documentMetadata(document);
    if (metadata.conversion && typeof metadata.conversion === "object" && !Array.isArray(metadata.conversion)) {
        return metadata.conversion;
    }

    return {
        required: Boolean(metadata.conversionRequired),
        succeeded: metadata.conversionSucceeded ?? null,
        convertedAt: metadata.conversionAt || null,
        engine: metadata.conversionEngine || null,
        engineVersion: metadata.conversionEngineVersion || null
    };
}

function documentIsDocxSource(document) {
    const metadata = documentMetadata(document);
    const sourceExtension = String(metadata.sourceExtension || document?.originalFileName || "").toLowerCase();
    const sourceMimeType = String(metadata.sourceMimeType || "").toLowerCase();

    return sourceExtension.endsWith(".docx") || sourceMimeType.includes("wordprocessingml.document");
}

function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleString();
}

function documentConversionSummary(document) {
    const conversion = documentConversion(document);
    if (!conversion.required) return "Native PDF signing copy";

    const convertedAt = formatDateTime(conversion.convertedAt);
    const engine = [conversion.engine, conversion.engineVersion].filter(Boolean).join(" ");
    if (convertedAt && engine) return `Converted ${convertedAt} with ${engine}`;
    if (convertedAt) return `Converted ${convertedAt}`;
    if (engine) return `Converted with ${engine}`;
    return conversion.succeeded ? "Converted to PDF signing copy" : "Conversion pending";
}

function documentPageSizes(document) {
    const metadata = documentMetadata(document);
    return Array.isArray(metadata.pageSizes) ? metadata.pageSizes : [];
}

function documentPageCount(document) {
    const metadata = documentMetadata(document);
    return Number(document?.pageCount || metadata.pageCount || documentPageSizes(document).length || 0);
}

function documentDimensionsSummary(document) {
    const pages = documentPageSizes(document);
    if (pages.length < 1) return "Dimensions pending";

    const first = pages[0];
    const width = Math.round(Number(first.displayWidth || first.width || 0));
    const height = Math.round(Number(first.displayHeight || first.height || 0));
    return width && height ? `${width} x ${height}` : "Dimensions pending";
}

function documentOrientationSummary(document) {
    const pages = documentPageSizes(document);
    if (pages.length < 1) return "Orientation pending";

    const orientations = new Set(pages.map((page) => page.displayOrientation || page.orientation).filter(Boolean));
    if (orientations.size === 1) return [...orientations][0];
    return "mixed";
}

function documentRotationSummary(document) {
    const pages = documentPageSizes(document);
    if (pages.length < 1) return "Rotation pending";

    const rotated = pages.filter((page) => Number(page.rotation || 0) !== 0);
    if (rotated.length < 1) return "rotation normalized";
    return `${rotated.length} rotated page${rotated.length === 1 ? "" : "s"} detected`;
}

function filenameWithSuffix(document, suffix) {
    const title = documentTitle(document).replace(/\.[^.]+$/, "").trim() || "document";
    return `${title} ${suffix}.pdf`;
}

function originalDownloadFilename(document) {
    return document?.originalFileName || documentTitle(document);
}

function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const anchor = window.document.createElement("a");
    anchor.href = url;
    anchor.download = filename || "document.pdf";
    window.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
}

function recipientName(recipient) {
    return [recipient?.name, recipient?.email ? `<${recipient.email}>` : ""].filter(Boolean).join(" ");
}

function isDigitalSigningContext(recipient) {
    const address = recipient?.metadata?.digitalContext?.address || recipient?.email || "";
    return recipient?.recipientType === "signer" && String(address).trim().toLowerCase().endsWith("@sign.invalid");
}

function truncateText(value, maxLength = 160) {
    const text = String(value || "").trim();
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 3)}...`;
}

function signatureEventLabel(eventType) {
    const value = String(eventType || "").trim();
    if (!value) return "Activity";

    return value
        .split(".")
        .filter(Boolean)
        .map((part) => part
            .split("_")
            .filter(Boolean)
            .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
            .join(" "))
        .join(" ");
}

function metadataValueSummary(value) {
    if (value === null || value === undefined || value === "") return "";
    if (Array.isArray(value)) return truncateText(value.filter(Boolean).join(", "));
    if (typeof value === "object") {
        try {
            return truncateText(JSON.stringify(value));
        } catch {
            return "";
        }
    }
    return truncateText(value);
}

function signatureEventDetails(event) {
    const metadata = objectMetadata(event);
    const metadataText = Object.entries(metadata)
        .map(([key, value]) => {
            const summary = metadataValueSummary(value);
            return summary ? `${key}: ${summary}` : "";
        })
        .filter(Boolean)
        .slice(0, 4)
        .join("; ");
    return [
        metadataText,
        event?.ipAddress ? `IP: ${event.ipAddress}` : "",
        event?.userAgent ? `User agent: ${truncateText(event.userAgent, 120)}` : ""
    ].filter(Boolean).join("; ");
}

function personDisplayName(person) {
    return person?.displayName
        || [person?.firstName, person?.middleName, person?.lastName].filter(Boolean).join(" ")
        || person?.name
        || person?.email
        || "";
}

function personEmail(person) {
    return person?.email || person?.primaryEmail || "";
}

function fieldTypeLabel(fieldType) {
    return FIELD_TYPES.find((type) => type.value === fieldType)?.label || fieldType || "Field";
}

function standardFieldDefinition(fieldType) {
    return STANDARD_FIELDS.find((field) => field.value === fieldType) || {
        value: fieldType,
        label: fieldTypeLabel(fieldType),
        width: 0.26,
        height: 0.05
    };
}

function signerColor(recipientUuid, signers) {
    const index = signers.findIndex((recipient) => recipient.uuid === recipientUuid);
    if (index < 0) return DEFAULT_FIELD_COLOR;
    return SIGNER_COLORS[index % SIGNER_COLORS.length];
}

function normalizedNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
}

function fieldDecimal(value, fallback = 0) {
    return Number(normalizedNumber(value, fallback).toFixed(3));
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function normalizeFieldMetadata(metadata = {}) {
    let source = metadata;
    if (typeof source === "string") {
        try {
            source = JSON.parse(source);
        } catch {
            source = {};
        }
    }
    const base = source && typeof source === "object" && !Array.isArray(source) ? source : {};

    const textAlign = FIELD_ALIGNMENTS.some((item) => item.value === base?.textAlign)
        ? base.textAlign
        : DEFAULT_FIELD_METADATA.textAlign;
    const fontFamily = FIELD_FONTS.some((item) => item.value === base?.fontFamily)
        ? base.fontFamily
        : DEFAULT_FIELD_METADATA.fontFamily;
    const fontSize = clamp(
        Math.round(normalizedNumber(base?.fontSize, DEFAULT_FIELD_METADATA.fontSize)),
        FIELD_FONT_SIZES[0],
        FIELD_FONT_SIZES[FIELD_FONT_SIZES.length - 1]
    );
    const name = normalizeFieldName(base.name || base.fieldName || "");
    const templateField = base.templateField && typeof base.templateField === "object" && !Array.isArray(base.templateField)
        ? base.templateField
        : {};
    const explicitApiName = templateField.name || base.apiName;
    const apiName = explicitApiName
        ? String(explicitApiName).trim().toLowerCase().slice(0, 128)
        : String(name || "")
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9._-]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 128);
    const normalized = { ...base, textAlign, fontFamily, fontSize };
    if (name) {
        normalized.name = name;
    } else {
        delete normalized.name;
        delete normalized.fieldName;
    }

    if (API_FIELD_NAME_PATTERN.test(apiName)) {
        normalized.apiName = apiName;
        normalized.templateField = { ...templateField, name: apiName };
    } else {
        delete normalized.apiName;
        if (apiName || Object.keys(templateField).length > 0) normalized.templateField = { ...templateField, name: apiName };
    }

    return normalized;
}

function fieldMetadataPayload(source) {
    return normalizeFieldMetadata(source);
}

function normalizeFieldName(value) {
    return String(value || "").trim().replace(/\s+/g, " ");
}

function fieldEffectiveName(field) {
    const metadata = normalizeFieldMetadata(field?.metadata);
    return normalizeFieldName(metadata.name || metadata.templateField?.name || field?.label || fieldTypeLabel(field?.fieldType));
}

function uniqueFieldName(baseName, existingFields = [], { documentUuid = "", ignoreUuids = [] } = {}) {
    const base = normalizeFieldName(baseName) || "Field";
    const ignored = new Set(ignoreUuids.filter(Boolean));
    const used = new Set(
        existingFields
            .filter((field) => !documentUuid || String(field.documentUuid || "") === String(documentUuid))
            .filter((field) => !ignored.has(field.uuid))
            .map((field) => fieldEffectiveName(field).toLowerCase())
            .filter(Boolean)
    );

    if (!used.has(base.toLowerCase())) return base;
    for (let index = 2; index < 1000; index += 1) {
        const candidate = `${base} ${index}`;
        if (!used.has(candidate.toLowerCase())) return candidate;
    }
    return `${base} ${Date.now()}`;
}

function duplicateFieldNameCount(name, existingFields = [], { documentUuid = "", ignoreUuids = [] } = {}) {
    const normalized = normalizeFieldName(name).toLowerCase();
    if (!normalized) return 0;
    const ignored = new Set(ignoreUuids.filter(Boolean));
    return existingFields.filter((field) => (
        (!documentUuid || String(field.documentUuid || "") === String(documentUuid)) &&
        !ignored.has(field.uuid) &&
        fieldEffectiveName(field).toLowerCase() === normalized
    )).length;
}

function fieldMetadataForName(field, overrides = {}, name = "") {
    const existing = normalizeFieldMetadata(field?.metadata || DEFAULT_FIELD_METADATA);
    const hasApiNameOverride = Object.prototype.hasOwnProperty.call(overrides, "apiName");
    const apiName = hasApiNameOverride
        ? String(overrides.apiName || "").trim().toLowerCase().slice(0, 128)
        : existing.apiName || existing.templateField?.name || "";
    const nextTemplateField = {
        ...(existing.templateField || {}),
        name: apiName
    };
    return fieldMetadataPayload({
        ...existing,
        ...overrides,
        name: normalizeFieldName(name || fieldEffectiveName(field)),
        apiName,
        templateField: nextTemplateField
    });
}

function selectedFieldFormComparableValue(field, value) {
    if (SELECTED_FIELD_GEOMETRY_FIELDS.has(field)) {
        return fieldDecimal(value, EMPTY_SELECTED_FIELD[field]);
    }
    if (field === "pageNumber") {
        return Math.max(1, Math.round(normalizedNumber(value, EMPTY_SELECTED_FIELD.pageNumber)));
    }
    if (field === "fontSize") {
        return clamp(
            Math.round(normalizedNumber(value, DEFAULT_FIELD_METADATA.fontSize)),
            FIELD_FONT_SIZES[0],
            FIELD_FONT_SIZES[FIELD_FONT_SIZES.length - 1]
        );
    }
    if (field === "required") {
        return Boolean(value);
    }
    return String(value ?? "");
}

function selectedFieldFormChangedKeys(form, baseline, allowedFields = SELECTED_FIELD_FORM_FIELDS) {
    const allowed = allowedFields instanceof Set ? allowedFields : new Set(allowedFields);
    const source = form || {};
    const comparison = baseline || {};

    return SELECTED_FIELD_FORM_FIELDS.filter((field) => (
        allowed.has(field) &&
        selectedFieldFormComparableValue(field, source[field]) !== selectedFieldFormComparableValue(field, comparison[field])
    ));
}

function selectedFieldUpdateItemsFromForm(form, selectedField, selectedFields = [], options = {}) {
    if (!selectedField) return [];
    const fieldValues = { ...form };
    const { textAlign, fontFamily, fontSize, validation, apiName } = fieldValues;
    delete fieldValues.textAlign;
    delete fieldValues.fontFamily;
    delete fieldValues.fontSize;
    delete fieldValues.validation;
    delete fieldValues.apiName;
    delete fieldValues.fieldType;
    const textMetadata = { textAlign, fontFamily, fontSize, apiName };

    if (selectedFields.length > 1) {
        const changedFields = Array.isArray(options.changedFields)
            ? options.changedFields.filter((field) => MULTI_SELECTED_FIELD_SYNC_FIELDS.has(field))
            : Array.from(MULTI_SELECTED_FIELD_SYNC_FIELDS);
        if (changedFields.length < 1) return [];

        return selectedFields
            .map((field) => {
                const values = {};
                const metadata = {};

                changedFields.forEach((changedField) => {
                    if (SELECTED_FIELD_METADATA_FIELDS.has(changedField)) {
                        metadata[changedField] = textMetadata[changedField];
                        return;
                    }
                    if (Object.prototype.hasOwnProperty.call(fieldValues, changedField)) {
                        values[changedField] = fieldValues[changedField];
                    }
                });

                if (Object.keys(metadata).length > 0) {
                    values.metadata = fieldMetadataForName(field, metadata);
                }

                return {
                    fieldUuid: field.uuid,
                    values
                };
            })
            .filter((item) => Object.keys(item.values).length > 0);
    }

    const name = normalizeFieldName(fieldValues.label) || fieldEffectiveName(selectedField);
    return [{
        fieldUuid: selectedField.uuid,
        values: {
            ...fieldValues,
            label: name,
            validation: fieldValidationPayload(validation),
            metadata: fieldMetadataForName(selectedField, textMetadata, name)
        }
    }];
}

function selectedFieldUpdateSignature(items) {
    return JSON.stringify(items.map((item) => ({
        fieldUuid: item.fieldUuid,
        values: item.values
    })));
}

function fieldFontCss(fontFamily) {
    return FIELD_FONTS.find((font) => font.value === fontFamily)?.css || FIELD_FONTS[0].css;
}

function fieldFlexAlignment(textAlign) {
    if (textAlign === "right") return "flex-end";
    if (textAlign === "center") return "center";
    return "flex-start";
}

function selectOption(options, value) {
    return options.find((option) => String(option.value) === String(value)) || null;
}

function listText(value) {
    if (Array.isArray(value)) return value.join(", ");
    return String(value || "");
}

function mergedListText(...values) {
    const seen = new Set();
    const items = [];
    values
        .flatMap((value) => listText(value).split(","))
        .map((value) => value.trim())
        .filter(Boolean)
        .forEach((value) => {
            const key = value.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            items.push(value);
        });
    return items.join(", ");
}

function selectMenuPortalTarget() {
    return typeof document !== "undefined" ? document.body : null;
}

function fieldGeometry(field) {
    return {
        x: normalizedNumber(field.x, 0),
        y: normalizedNumber(field.y, 0),
        width: normalizedNumber(field.width, 0.26),
        height: normalizedNumber(field.height, 0.05)
    };
}

function roundedPlacement(placement) {
    return Object.keys(placement).reduce((out, key) => {
        out[key] = Number(Number(placement[key]).toFixed(4));
        return out;
    }, {});
}

function snapNumber(value, step = FIELD_GRID_STEP) {
    const number = normalizedNumber(value, 0);
    if (!step) return number;
    return Number((Math.round(number / step) * step).toFixed(4));
}

function snapFieldPlacement(values, step = FIELD_GRID_STEP) {
    return Object.keys(values || {}).reduce((out, key) => {
        out[key] = ["x", "y", "width", "height"].includes(key) ? snapNumber(values[key], step) : values[key];
        return out;
    }, {});
}

function clampFieldPlacement(values) {
    const placement = { ...values };
    if (Object.prototype.hasOwnProperty.call(placement, "width")) {
        placement.width = clamp(normalizedNumber(placement.width, 0.26), MIN_FIELD_SIZE, 1);
    }
    if (Object.prototype.hasOwnProperty.call(placement, "height")) {
        placement.height = clamp(normalizedNumber(placement.height, 0.05), MIN_FIELD_SIZE, 1);
    }
    if (Object.prototype.hasOwnProperty.call(placement, "x")) {
        placement.x = clamp(normalizedNumber(placement.x, 0), 0, Math.max(0, 1 - normalizedNumber(placement.width, 0)));
    }
    if (Object.prototype.hasOwnProperty.call(placement, "y")) {
        placement.y = clamp(normalizedNumber(placement.y, 0), 0, Math.max(0, 1 - normalizedNumber(placement.height, 0)));
    }
    return roundedPlacement(placement);
}

function fieldClonePayload(field, overrides = {}) {
    const label = overrides.label || field.label || fieldEffectiveName(field) || fieldTypeLabel(field.fieldType);
    const source = {
        documentUuid: field.documentUuid,
        recipientUuid: field.recipientUuid || "",
        fieldType: field.fieldType || "signature",
        pageNumber: Math.max(1, Math.round(normalizedNumber(field.pageNumber, 1))),
        ...fieldGeometry(field),
        required: field.required !== false,
        label,
        placeholder: field.placeholder || null,
        defaultValue: field.defaultValue ?? null,
        options: Array.isArray(field.options)
            ? field.options
            : (["radio", "dropdown"].includes(field.fieldType) ? [] : null),
        validation: field.validation || null,
        fieldOrder: field.fieldOrder ?? null,
        metadata: fieldMetadataForName(field, {}, label),
        ...overrides
    };

    source.metadata = fieldMetadataPayload({
        ...source.metadata,
        ...normalizeFieldMetadata(overrides.metadata || {}),
        name: source.label
    });

    delete source.uuid;
    delete source.envelopeUuid;
    delete source.value;
    delete source.completedAt;

    return normalizeFieldPayload(source);
}

function fieldPatchSnapshot(field) {
    return fieldClonePayload(field, {
        documentUuid: field.documentUuid,
        recipientUuid: field.recipientUuid || ""
    });
}

function fieldsHaveSameLayout(field, payload) {
    return (
        String(field.documentUuid || "") === String(payload.documentUuid || "") &&
        Number(field.pageNumber || 1) === Number(payload.pageNumber || 1) &&
        String(field.recipientUuid || "") === String(payload.recipientUuid || "") &&
        String(field.fieldType || "") === String(payload.fieldType || "") &&
        Math.abs(normalizedNumber(field.x, 0) - normalizedNumber(payload.x, 0)) < 0.0001 &&
        Math.abs(normalizedNumber(field.y, 0) - normalizedNumber(payload.y, 0)) < 0.0001 &&
        Math.abs(normalizedNumber(field.width, 0) - normalizedNumber(payload.width, 0)) < 0.0001 &&
        Math.abs(normalizedNumber(field.height, 0) - normalizedNumber(payload.height, 0)) < 0.0001
    );
}

function shouldIgnoreFieldNudgeTarget(target) {
    const element = typeof Element !== "undefined" && target instanceof Element ? target : null;
    if (!element) return false;
    if (element.closest("[data-signature-field]")) return false;
    return Boolean(element.closest("input, textarea, select, button, a, [contenteditable='true'], .react-select__control, .react-select__menu"));
}

function resizeFieldGeometry(start, handle, dx, dy) {
    let { x, y, width, height } = start;

    if (handle.includes("e")) {
        width = clamp(start.width + dx, MIN_FIELD_SIZE, 1 - start.x);
    }

    if (handle.includes("s")) {
        height = clamp(start.height + dy, MIN_FIELD_SIZE, 1 - start.y);
    }

    if (handle.includes("w")) {
        const right = start.x + start.width;
        x = clamp(start.x + dx, 0, right - MIN_FIELD_SIZE);
        width = right - x;
    }

    if (handle.includes("n")) {
        const bottom = start.y + start.height;
        y = clamp(start.y + dy, 0, bottom - MIN_FIELD_SIZE);
        height = bottom - y;
    }

    return roundedPlacement({ x, y, width, height });
}

function rectanglesIntersect(a, b) {
    return (
        a.x <= b.x + b.width &&
        a.x + a.width >= b.x &&
        a.y <= b.y + b.height &&
        a.y + a.height >= b.y
    );
}

function selectionBoxFromPoints(start, current) {
    return roundedPlacement({
        x: Math.min(start.x, current.x),
        y: Math.min(start.y, current.y),
        width: Math.abs(current.x - start.x),
        height: Math.abs(current.y - start.y)
    });
}

function normalizeFieldPayload(payload) {
    const normalized = { ...payload };

    if ("pageNumber" in normalized) {
        normalized.pageNumber = Math.max(1, Math.round(normalizedNumber(normalized.pageNumber, 1)));
    }

    if ("width" in normalized) {
        normalized.width = clamp(normalizedNumber(normalized.width, 0.26), 0.02, 1);
    }

    if ("height" in normalized) {
        normalized.height = clamp(normalizedNumber(normalized.height, 0.05), 0.02, 1);
    }

    if ("x" in normalized) {
        const maxX = "width" in normalized ? 1 - normalized.width : 1;
        normalized.x = clamp(normalizedNumber(normalized.x, 0), 0, Math.max(0, maxX));
    }

    if ("y" in normalized) {
        const maxY = "height" in normalized ? 1 - normalized.height : 1;
        normalized.y = clamp(normalizedNumber(normalized.y, 0), 0, Math.max(0, maxY));
    }

    if ("required" in normalized) {
        normalized.required = Boolean(normalized.required);
    }

    if ("metadata" in normalized) {
        normalized.metadata = fieldMetadataPayload(normalized.metadata);
    }

    return normalized;
}

function newRecipientRow() {
    return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: "",
        email: "",
        isRequired: true
    };
}

function initialRecipientRows() {
    return [newRecipientRow()];
}

function splitRecipientRows(rows) {
    const complete = [];
    const partial = [];

    rows.forEach((row) => {
        const name = String(row.name || "").trim();
        const email = String(row.email || "").trim();
        if (!name && !email) return;
        if (name && email) {
            complete.push({ ...row, name, email });
            return;
        }
        partial.push(row);
    });

    return { complete, partial };
}

const PDF_PAGE_TARGET_WIDTH = 760;
const PDF_THUMBNAIL_WIDTH = 92;
const FIELD_SIDEBAR_STYLE = {
    position: "sticky",
    top: 84,
    maxHeight: "calc(100vh - 96px)",
    overflowY: "auto"
};

const RecipientActionToggle = React.forwardRef(({ onClick, disabled }, ref) => (
    <Button
        ref={ref}
        type="button"
        variant="outline-secondary"
        size="sm"
        className="btn-icon btn-icon-only shadow-none"
        disabled={disabled}
        onClick={(event) => {
            event.preventDefault();
            onClick?.(event);
        }}
    >
        <CsLineIcons icon="more-horizontal" size="16" />
    </Button>
));

RecipientActionToggle.displayName = "RecipientActionToggle";

function RecipientSuggestInput({ label, type = "text", value, disabled, onChange, onSelect }) {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        const query = String(searchQuery || "").trim();
        if (disabled || query.length < 2) {
            setSuggestions([]);
            setLoading(false);
            setOpen(false);
            return undefined;
        }

        let cancelled = false;
        const timeout = window.setTimeout(async () => {
            setLoading(true);
            try {
                const response = await signApi.get(`/people/p/0/s/8/o/lastName/asc/f/0/${encodeURIComponent(query)}`, {
                    params: { archived: 0 }
                });
                if (!cancelled) {
                    setSuggestions(Array.isArray(response.data?.items) ? response.data.items : []);
                    setOpen(true);
                }
            } catch {
                if (!cancelled) {
                    setSuggestions([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }, 250);

        return () => {
            cancelled = true;
            window.clearTimeout(timeout);
        };
    }, [disabled, searchQuery]);

    return (
        <div className="top-label mb-0" style={{ position: "relative" }}>
            <Form.Control
                type={type}
                value={value}
                disabled={disabled}
                autoComplete="off"
                onChange={(event) => {
                    setSearchQuery(event.target.value);
                    onChange(event.target.value);
                }}
                onFocus={() => {
                    setSearchQuery(value);
                    setOpen(true);
                }}
                onBlur={() => window.setTimeout(() => setOpen(false), 120)}
            />
            <label>{label}</label>
            {open && (loading || suggestions.length > 0) ? (
                <div
                    className="dropdown-menu show w-100"
                    style={{
                        maxHeight: 240,
                        overflowY: "auto",
                        top: "calc(100% + 2px)",
                        zIndex: 1060
                    }}
                >
                    {loading ? <span className="dropdown-item-text text-muted">Searching...</span> : null}
                    {suggestions.map((person) => {
                        const name = personDisplayName(person);
                        const email = personEmail(person);
                        return (
                            <button
                                key={person.uuid || `${name}-${email}`}
                                type="button"
                                className="dropdown-item"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    setSearchQuery("");
                                    setSuggestions([]);
                                    onSelect(person);
                                    setOpen(false);
                                }}
                            >
                                <span className="d-block fw-semibold">{name || email}</span>
                                {email ? <span className="d-block small text-muted">{email}</span> : null}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

function TagTermsEditor({ value, disabled, onChange }) {
    const [draft, setDraft] = useState("");
    const terms = useMemo(() => tagTerms(value), [value]);

    const commitDraft = () => {
        const term = normalizeTagTerm(draft);
        if (!term) return;
        onChange(tagTermsText([...terms, term]));
        setDraft("");
    };

    const removeTerm = (term) => {
        onChange(tagTermsText(terms.filter((item) => item !== term)));
    };

    const removeLastTerm = () => {
        if (terms.length === 0) return;
        onChange(tagTermsText(terms.slice(0, -1)));
    };

    return (
        <div className="top-label mb-3">
            <div
                className={`form-control h-auto d-flex flex-wrap align-items-center gap-2 ${disabled ? "bg-light" : ""}`}
                style={{ minHeight: 44 }}
            >
                {terms.map((term) => (
                    <span key={term} className="badge bg-light text-dark border d-inline-flex align-items-center gap-1">
                        {term}
                        <button
                            type="button"
                            className="btn btn-link p-0 lh-1 text-muted"
                            aria-label={`Remove ${term}`}
                            disabled={disabled}
                            onClick={() => removeTerm(term)}
                            style={{ fontSize: 16, textDecoration: "none" }}
                        >
                            x
                        </button>
                    </span>
                ))}
                <input
                    type="text"
                    value={draft}
                    disabled={disabled}
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={commitDraft}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === ",") {
                            event.preventDefault();
                            commitDraft();
                            return;
                        }
                        if ((event.key === "Backspace" || event.key === "Delete") && !draft) {
                            event.preventDefault();
                            removeLastTerm();
                        }
                    }}
                    style={{
                        minWidth: 140,
                        flex: "1 1 140px",
                        border: 0,
                        outline: 0,
                        background: "transparent"
                    }}
                />
            </div>
            <label>TAGS</label>
        </div>
    );
}

function AlignmentIconButtons({ value = "", disabled = false, onChange, activeSelection = true, alignments = FIELD_ALIGNMENTS }) {
    return (
        <div className="btn-group" role="group" aria-label="Alignment">
            {alignments.map((alignment) => (
                <Button
                    key={alignment.value}
                    type="button"
                    variant={activeSelection && value === alignment.value ? "primary" : "outline-secondary"}
                    size="sm"
                    disabled={disabled}
                    title={`Align ${alignment.label.toLowerCase()}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onChange(alignment.value)}
                >
                    <span style={alignment.iconStyle || undefined}>
                        <CsLineIcons icon={alignment.icon} size="16" />
                    </span>
                </Button>
            ))}
        </div>
    );
}

function PdfRenderedPage({ page, width, height }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !page) return undefined;

        const viewport = page.getViewport({
            scale: width / page.getViewport({ scale: 1 }).width
        });
        const context = canvas.getContext("2d");
        const outputScale = window.devicePixelRatio || 1;

        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const renderTask = page.render({
            canvasContext: context,
            viewport,
            transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null
        });
        let cancelled = false;

        renderTask.promise.catch((err) => {
            if (!cancelled && err?.name !== "RenderingCancelledException") {
                console.error(err);
            }
        });

        return () => {
            cancelled = true;
            renderTask.cancel();
        };
    }, [page, width]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                display: "block",
                width,
                height,
                background: "#fff"
            }}
        />
    );
}

function FieldOverlayItem({
    field,
    index,
    signers,
    selected,
    editable,
    onSelectField,
    onDeleteField,
    onFieldDragStart,
    onFieldDragEnd,
    onResizeField
}) {
    const color = signerColor(field.recipientUuid, signers);
    const metadata = normalizeFieldMetadata(field.metadata);
    const [resizePreview, setResizePreview] = useState(null);
    const displayField = { ...field, ...(resizePreview || {}) };

    useEffect(() => {
        setResizePreview(null);
    }, [field.uuid, field.x, field.y, field.width, field.height]);

    const handleResizeStart = (event, handle) => {
        if (!editable) return;
        event.preventDefault();
        event.stopPropagation();
        onSelectField(field.uuid);

        const pageElement = event.currentTarget.closest("[data-signature-page]");
        const pageRect = pageElement?.getBoundingClientRect();
        if (!pageRect?.width || !pageRect?.height) return;

        const start = fieldGeometry(displayField);
        const startClientX = event.clientX;
        const startClientY = event.clientY;
        let nextPlacement = start;

        const onPointerMove = (moveEvent) => {
            moveEvent.preventDefault();
            const dx = (moveEvent.clientX - startClientX) / pageRect.width;
            const dy = (moveEvent.clientY - startClientY) / pageRect.height;
            nextPlacement = resizeFieldGeometry(start, handle, dx, dy);
            setResizePreview(nextPlacement);
        };

        const onPointerUp = () => {
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
            if (
                nextPlacement.x !== start.x ||
                nextPlacement.y !== start.y ||
                nextPlacement.width !== start.width ||
                nextPlacement.height !== start.height
            ) {
                onResizeField?.(field.uuid, nextPlacement);
                window.setTimeout(() => setResizePreview(null), 1500);
            }
        };

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp, { once: true });
    };

    return (
        <div
            data-signature-field
            role="button"
            tabIndex={0}
            draggable={editable}
            onDragStart={(event) => onFieldDragStart(event, field)}
            onDragEnd={onFieldDragEnd}
            onClick={(event) => {
                event.stopPropagation();
                onSelectField(field.uuid, { additive: event.ctrlKey || event.metaKey });
            }}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectField(field.uuid);
                }
                if ((event.key === "Delete" || event.key === "Backspace") && editable) {
                    event.preventDefault();
                    event.stopPropagation();
                    onDeleteField?.(field.uuid);
                }
            }}
            style={{
                position: "absolute",
                left: `${normalizedNumber(displayField.x, 0) * 100}%`,
                top: `${normalizedNumber(displayField.y, 0) * 100}%`,
                width: `${normalizedNumber(displayField.width, 0.26) * 100}%`,
                height: `${normalizedNumber(displayField.height, 0.05) * 100}%`,
                border: `2px solid ${color.border}`,
                background: selected ? color.selected : color.background,
                color: color.text,
                borderRadius: 4,
                boxShadow: selected ? "0 0 0 3px rgba(13, 110, 253, 0.18)" : "0 1px 3px rgba(0, 0, 0, 0.16)",
                cursor: editable ? "move" : "default",
                display: "flex",
                alignItems: "center",
                justifyContent: fieldFlexAlignment(metadata.textAlign),
                padding: "2px 6px",
                fontFamily: fieldFontCss(metadata.fontFamily),
                fontSize: metadata.fontSize,
                fontWeight: 600,
                textAlign: metadata.textAlign,
                userSelect: "none",
                overflow: selected && editable ? "visible" : "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                zIndex: selected ? 1000 : 10 + index
            }}
            title={`${field.label || fieldTypeLabel(field.fieldType)} - ${recipientName(signers.find((recipient) => recipient.uuid === field.recipientUuid))}`}
        >
            <span
                style={{
                    display: "block",
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    pointerEvents: "none"
                }}
            >
                {field.label || fieldTypeLabel(field.fieldType)}
            </span>
            {selected && editable ? FIELD_RESIZE_HANDLES.map((handle) => (
                <button
                    key={handle.key}
                    type="button"
                    aria-label={`Resize ${handle.key}`}
                    onPointerDown={(event) => handleResizeStart(event, handle.key)}
                    style={{
                        position: "absolute",
                        width: 12,
                        height: 12,
                        padding: 0,
                        borderRadius: 2,
                        border: `2px solid ${color.border}`,
                        background: "#fff",
                        cursor: handle.cursor,
                        zIndex: 1001,
                        ...handle.style
                    }}
                />
            )) : null}
        </div>
    );
}

function SignatureDocumentCanvas({
    envelopeUuid,
    document,
    fields,
    signers,
    selectedFieldUuids,
    activePageNumber,
    onActivePageChange,
    editable,
    snapToGrid,
    onSelectField,
    onSelectFields,
    onDropNewField,
    onMoveField,
    onDeleteField,
    onResizeField,
    onDropComplete
}) {
    const [pages, setPages] = useState([]);
    const [loadingPreview, setLoadingPreview] = useState(true);
    const [error, setError] = useState("");
    const [zoom, setZoom] = useState(1);
    const [selectionBox, setSelectionBox] = useState(null);
    const containerRef = useRef(null);
    const pageRefs = useRef(new Map());
    const scrollFrameRef = useRef(null);
    const programmaticScrollRef = useRef(false);
    const programmaticScrollTimerRef = useRef(null);
    const activePageUpdateSourceRef = useRef("");
    const suppressNextPageClickRef = useRef(false);
    const activeSelectionRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        let loadingTask = null;

        const load = async () => {
            if (!envelopeUuid || !document?.uuid) return;
            setLoadingPreview(true);
            setError("");
            setPages([]);

            try {
                const response = await signApi.get(`/signature/envelopes/${envelopeUuid}/documents/${document.uuid}/content`, {
                    responseType: "arraybuffer"
                });
                const [pdfjsLib] = await Promise.all([
                    import("pdfjs-dist/legacy/build/pdf"),
                    import("pdfjs-dist/legacy/build/pdf.worker.entry")
                ]);
                loadingTask = pdfjsLib.getDocument({ data: response.data, disableWorker: true });
                const pdf = await loadingTask.promise;
                const items = [];

                for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
                    const page = await pdf.getPage(pageNumber);
                    const viewport = page.getViewport({ scale: 1 });
                    const width = PDF_PAGE_TARGET_WIDTH;
                    const height = width * (viewport.height / viewport.width);
                    items.push({ pageNumber, page, width, height });
                }

                if (!cancelled) {
                    setPages(items);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.response?.data?.error || err.message || "Unable to preview this document.");
                }
            } finally {
                if (!cancelled) {
                    setLoadingPreview(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
            loadingTask?.destroy?.();
        };
    }, [envelopeUuid, document?.uuid]);

    useEffect(() => {
        if (pages.length === 0) return;
        const hasActivePage = pages.some((page) => page.pageNumber === activePageNumber);
        if (!hasActivePage) {
            onActivePageChange?.(pages[0].pageNumber);
        }
    }, [activePageNumber, onActivePageChange, pages]);

    useEffect(() => () => {
        if (scrollFrameRef.current) {
            window.cancelAnimationFrame(scrollFrameRef.current);
        }
        if (programmaticScrollTimerRef.current) {
            window.clearTimeout(programmaticScrollTimerRef.current);
        }
    }, []);

    useEffect(() => {
        const updateSource = activePageUpdateSourceRef.current;
        activePageUpdateSourceRef.current = "";
        if (updateSource === "scroll" || updateSource === "page-interaction") return;
        if (!activePageNumber) return;
        const pageElement = pageRefs.current.get(activePageNumber);
        const container = containerRef.current;
        if (!pageElement || !container) return;

        const pageTop = pageElement.offsetTop;
        const pageBottom = pageTop + pageElement.offsetHeight;
        const viewportTop = container.scrollTop;
        const viewportBottom = viewportTop + container.clientHeight;
        const visibleHeight = Math.max(0, Math.min(pageBottom, viewportBottom) - Math.max(pageTop, viewportTop));
        const enoughVisible = visibleHeight >= (Math.min(pageElement.offsetHeight, container.clientHeight) * 0.66);
        if (enoughVisible) return;

        programmaticScrollRef.current = true;
        if (programmaticScrollTimerRef.current) {
            window.clearTimeout(programmaticScrollTimerRef.current);
        }
        container.scrollTo({
            top: Math.max(pageTop - 16, 0),
            behavior: "smooth"
        });
        programmaticScrollTimerRef.current = window.setTimeout(() => {
            programmaticScrollRef.current = false;
        }, 450);
    }, [activePageNumber, zoom, pages.length]);

    const setActivePage = (pageNumber, source) => {
        activePageUpdateSourceRef.current = Number(pageNumber) !== Number(activePageNumber) ? source : "";
        onActivePageChange?.(pageNumber);
    };

    const scrollToPage = (pageNumber) => {
        setActivePage(pageNumber, "navigation");
    };

    const updateActivePageFromScroll = () => {
        if (programmaticScrollRef.current) return;
        if (scrollFrameRef.current) return;
        scrollFrameRef.current = window.requestAnimationFrame(() => {
            scrollFrameRef.current = null;
            const container = containerRef.current;
            if (!container) return;

            const centerY = container.getBoundingClientRect().top + (container.clientHeight / 2);
            let closestPageNumber = activePageNumber || pages[0]?.pageNumber || 1;
            let closestDistance = Number.POSITIVE_INFINITY;
            pages.forEach((page) => {
                const element = pageRefs.current.get(page.pageNumber);
                if (!element) return;
                const rect = element.getBoundingClientRect();
                const distance = Math.abs((rect.top + (rect.height / 2)) - centerY);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestPageNumber = page.pageNumber;
                }
            });

            if (closestPageNumber !== activePageNumber) {
                activePageUpdateSourceRef.current = "scroll";
                onActivePageChange?.(closestPageNumber);
            }
        });
    };

    const handleFieldDragStart = (event, field) => {
        if (!editable) {
            event.preventDefault();
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        onSelectField(field.uuid);
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("application/x-existing-signature-field", field.uuid);
        event.dataTransfer.setData("application/x-signature-field-offset", JSON.stringify({
            x: clamp((event.clientX - rect.left) / rect.width, 0, 1),
            y: clamp((event.clientY - rect.top) / rect.height, 0, 1)
        }));
    };

    const handleFieldDragEnd = () => {
        onDropComplete?.();
    };

    const handlePageDrop = (event, pageNumber) => {
        event.preventDefault();
        event.stopPropagation();
        onDropComplete?.();
        setActivePage(pageNumber, "page-interaction");
        if (!editable) return;

        const rect = event.currentTarget.getBoundingClientRect();
        const relativeX = (event.clientX - rect.left) / rect.width;
        const relativeY = (event.clientY - rect.top) / rect.height;
        const existingFieldUuid = event.dataTransfer.getData("application/x-existing-signature-field");
        const fieldType = event.dataTransfer.getData("application/x-signature-field");

        if (existingFieldUuid) {
            const field = fields.find((item) => item.uuid === existingFieldUuid);
            if (!field) return;

            let offset = { x: 0.5, y: 0.5 };
            try {
                offset = JSON.parse(event.dataTransfer.getData("application/x-signature-field-offset")) || offset;
            } catch {
                offset = { x: 0.5, y: 0.5 };
            }

            const width = normalizedNumber(field.width, 0.26);
            const height = normalizedNumber(field.height, 0.05);
            onMoveField(existingFieldUuid, {
                pageNumber,
                x: clamp(relativeX - (offset.x * width), 0, Math.max(0, 1 - width)),
                y: clamp(relativeY - (offset.y * height), 0, Math.max(0, 1 - height))
            });
            return;
        }

        if (fieldType) {
            const definition = standardFieldDefinition(fieldType);
            onDropNewField({
                pageNumber,
                fieldType,
                label: definition.label,
                width: definition.width,
                height: definition.height,
                x: clamp(relativeX - (definition.width / 2), 0, Math.max(0, 1 - definition.width)),
                y: clamp(relativeY - (definition.height / 2), 0, Math.max(0, 1 - definition.height))
            });
        }
    };

    const handlePagePointerDown = (event, pageNumber) => {
        if (!editable || event.button !== 0) return;
        if (event.target.closest("[data-signature-field]")) return;
        event.currentTarget.focus({ preventScroll: true });
        setActivePage(pageNumber, "page-interaction");

        const pageRect = event.currentTarget.getBoundingClientRect();
        if (!pageRect?.width || !pageRect?.height) return;

        const start = {
            x: clamp((event.clientX - pageRect.left) / pageRect.width, 0, 1),
            y: clamp((event.clientY - pageRect.top) / pageRect.height, 0, 1)
        };
        const selection = {
            pageNumber,
            pageRect,
            start,
            additive: event.ctrlKey || event.metaKey,
            dragged: false,
            box: null
        };
        activeSelectionRef.current = selection;

        const onPointerMove = (moveEvent) => {
            const active = activeSelectionRef.current;
            if (!active) return;

            const current = {
                x: clamp((moveEvent.clientX - active.pageRect.left) / active.pageRect.width, 0, 1),
                y: clamp((moveEvent.clientY - active.pageRect.top) / active.pageRect.height, 0, 1)
            };
            const box = selectionBoxFromPoints(active.start, current);

            if (box.width < 0.004 && box.height < 0.004) return;

            active.dragged = true;
            active.box = box;
            suppressNextPageClickRef.current = true;
            setSelectionBox({
                pageNumber: active.pageNumber,
                ...box
            });
        };

        const onPointerUp = () => {
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
            const active = activeSelectionRef.current;
            activeSelectionRef.current = null;

            if (!active?.dragged || !active.box) {
                setSelectionBox(null);
                return;
            }

            const touched = fields
                .filter((field) => Number(field.pageNumber || 1) === active.pageNumber)
                .filter((field) => rectanglesIntersect(fieldGeometry(field), active.box))
                .map((field) => field.uuid);

            onSelectFields(touched, { additive: active.additive, focusEditor: true });
            setSelectionBox(null);
        };

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp, { once: true });
    };

    if (error) return <Alert variant="warning">{error}</Alert>;
    if (loadingPreview) return <div className="text-muted">Loading document preview...</div>;

    const activePage = pages.find((page) => page.pageNumber === activePageNumber) || pages[0] || null;
    const zoomPercent = Math.round(zoom * 100);
    const updateZoom = (direction) => {
        setZoom((current) => clamp(Number((current + (direction * PDF_ZOOM_STEP)).toFixed(2)), PDF_MIN_ZOOM, PDF_MAX_ZOOM));
    };

    return (
        <div
            style={{
                width: "100%",
                height: 720,
                borderRadius: 8,
                background: "#e9ecef",
                border: "1px solid #dee2e6",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}
        >
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-2 border-bottom bg-white">
                <div className="d-flex align-items-center gap-2">
                    <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        className="btn-icon btn-icon-only"
                        disabled={!activePage || activePage.pageNumber <= 1}
                        title="Previous page"
                        onClick={() => scrollToPage(Math.max(1, (activePage?.pageNumber || 1) - 1))}
                    >
                        <CsLineIcons icon="arrow-left" size="15" />
                    </Button>
                    <Badge bg="light" text="dark">
                        Page {activePage?.pageNumber || 1} of {pages.length}
                    </Badge>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        className="btn-icon btn-icon-only"
                        disabled={!activePage || activePage.pageNumber >= pages.length}
                        title="Next page"
                        onClick={() => scrollToPage(Math.min(pages.length, (activePage?.pageNumber || 1) + 1))}
                    >
                        <CsLineIcons icon="arrow-right" size="15" />
                    </Button>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        className="btn-icon btn-icon-only"
                        disabled={zoom <= PDF_MIN_ZOOM}
                        title="Zoom out"
                        onClick={() => updateZoom(-1)}
                    >
                        <CsLineIcons icon="minus" size="15" />
                    </Button>
                    <Badge bg="light" text="dark">{zoomPercent}%</Badge>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        className="btn-icon btn-icon-only"
                        disabled={zoom >= PDF_MAX_ZOOM}
                        title="Zoom in"
                        onClick={() => updateZoom(1)}
                    >
                        <CsLineIcons icon="plus" size="15" />
                    </Button>
                </div>
            </div>
            <div style={{ display: "flex", minHeight: 0, flex: "1 1 auto" }}>
                <div
                    style={{
                        width: 128,
                        flex: "0 0 128px",
                        overflowY: "auto",
                        borderRight: "1px solid #dee2e6",
                        background: "#f8f9fa",
                        padding: 10
                    }}
                >
                    <div className="d-flex flex-column gap-2">
                        {pages.map((page) => {
                            const thumbHeight = PDF_THUMBNAIL_WIDTH * (page.height / page.width);
                            const active = page.pageNumber === activePage?.pageNumber;
                            return (
                                <button
                                    key={`thumb-${page.pageNumber}`}
                                    type="button"
                                    className="p-1 bg-white"
                                    title={`Page ${page.pageNumber}`}
                                    onClick={() => scrollToPage(page.pageNumber)}
                                    style={{
                                        border: active ? "2px solid #0d6efd" : "1px solid #ced4da",
                                        borderRadius: 4,
                                        boxShadow: active ? "0 0 0 2px rgba(13, 110, 253, 0.12)" : "none",
                                        cursor: "pointer"
                                    }}
                                >
                                    <PdfRenderedPage page={page.page} width={PDF_THUMBNAIL_WIDTH} height={thumbHeight} />
                                    <div className="small text-muted mt-1">Page {page.pageNumber}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div
                    ref={containerRef}
                    onScroll={updateActivePageFromScroll}
                    style={{
                        minWidth: 0,
                        flex: "1 1 auto",
                        overflow: "auto",
                        padding: 16
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", minWidth: PDF_PAGE_TARGET_WIDTH * zoom }}>
                        {pages.map((page) => {
                            const pageFields = fields.filter((field) => Number(field.pageNumber || 1) === page.pageNumber);
                            const pageWidth = page.width * zoom;
                            const pageHeight = page.height * zoom;
                            return (
                                <div
                                    data-signature-page
                                    key={page.pageNumber}
                                    ref={(node) => {
                                        if (node) pageRefs.current.set(page.pageNumber, node);
                                        else pageRefs.current.delete(page.pageNumber);
                                    }}
                                    tabIndex={editable ? 0 : undefined}
                                    onPointerDown={(event) => handlePagePointerDown(event, page.pageNumber)}
                                    onDrop={(event) => handlePageDrop(event, page.pageNumber)}
                                    onDragOver={(event) => editable && event.preventDefault()}
                                    onClick={() => {
                                        setActivePage(page.pageNumber, "page-interaction");
                                        if (suppressNextPageClickRef.current) {
                                            suppressNextPageClickRef.current = false;
                                            return;
                                        }
                                        onSelectField("");
                                    }}
                                    style={{
                                        position: "relative",
                                        width: pageWidth,
                                        height: pageHeight,
                                        background: "#fff",
                                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.16)",
                                        flex: "0 0 auto",
                                        outline: "none"
                                    }}
                                >
                                    <PdfRenderedPage page={page.page} width={pageWidth} height={pageHeight} />
                                    {snapToGrid ? (
                                        <div
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                backgroundImage: `linear-gradient(to right, rgba(13, 110, 253, 0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(13, 110, 253, 0.12) 1px, transparent 1px)`,
                                                backgroundSize: `${FIELD_GRID_STEP * 100}% ${FIELD_GRID_STEP * 100}%`,
                                                pointerEvents: "none",
                                                zIndex: 1
                                            }}
                                        />
                                    ) : null}
                                    <div style={{ position: "absolute", inset: 0, zIndex: 2 }}>
                                        {pageFields.map((field, index) => (
                                            <FieldOverlayItem
                                                key={field.uuid}
                                                field={field}
                                                index={index}
                                                signers={signers}
                                                selected={(selectedFieldUuids || []).includes(field.uuid)}
                                                editable={editable}
                                                onSelectField={onSelectField}
                                                onDeleteField={onDeleteField}
                                                onFieldDragStart={handleFieldDragStart}
                                                onFieldDragEnd={handleFieldDragEnd}
                                                onResizeField={onResizeField}
                                            />
                                        ))}
                                    </div>
                                    {selectionBox?.pageNumber === page.pageNumber ? (
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: `${selectionBox.x * 100}%`,
                                                top: `${selectionBox.y * 100}%`,
                                                width: `${selectionBox.width * 100}%`,
                                                height: `${selectionBox.height * 100}%`,
                                                border: "1px dashed #0d6efd",
                                                background: "rgba(13, 110, 253, 0.12)",
                                                pointerEvents: "none",
                                                zIndex: 2000
                                            }}
                                        />
                                    ) : null}
                                    <Badge
                                        bg="dark"
                                        style={{
                                            position: "absolute",
                                            right: 8,
                                            bottom: 8,
                                            opacity: 0.72,
                                            zIndex: 3
                                        }}
                                    >
                                        {page.pageNumber}
                                    </Badge>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function SignatureEnvelopeDetail() {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const optionsInitializedRef = useRef(false);
    const fieldKeyboardTargetRef = useRef(null);
    const addFileInputRef = useRef(null);
    const replaceFileInputRef = useRef(null);
    const activeDocumentUuidRef = useRef("");
    const fieldsRef = useRef([]);
    const selectedFieldUuidsRef = useRef([]);
    const pendingFieldUpdatesRef = useRef(new Map());
    const fieldPersistingRef = useRef(false);
    const recipientDragRef = useRef(null);
    const editorShortcutsRef = useRef({});
    const selectedFieldFormBaselineRef = useRef(EMPTY_SELECTED_FIELD);
    const selectedFieldFormSnapshotRef = useRef("");
    const selectedFieldAutoSaveTimerRef = useRef(null);
    const copyFeedbackTimerRef = useRef(null);
    const sendInFlightRef = useRef(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [envelope, setEnvelope] = useState(null);
    const [builderStep, setBuilderStep] = useState("signers");
    const [signerRows, setSignerRows] = useState(() => initialRecipientRows());
    const [ccRows, setCcRows] = useState(() => initialRecipientRows());
    const [fieldForm, setFieldForm] = useState(DEFAULT_FIELD);
    const [selectedFieldUuid, setSelectedFieldUuid] = useState("");
    const [activeDocumentUuid, setActiveDocumentUuid] = useState("");
    const [selectedFieldUuids, setSelectedFieldUuids] = useState([]);
    const [selectedFieldForm, setSelectedFieldForm] = useState(EMPTY_SELECTED_FIELD);
    const [optionsForm, setOptionsForm] = useState({
        title: "",
        subject: "",
        messageBody: "",
        internalNotes: "",
        externalNotes: "",
        expirationAt: "",
        defaultReminderFrequency: "daily",
        defaultReminderEndsAt: "",
        routingMode: "parallel",
        category: "",
        practiceArea: "",
        documentType: "",
        tags: ""
    });
    const [cancelDraftModal, setCancelDraftModal] = useState({
        show: false,
        confirmation: ""
    });
    const [removeDocumentModal, setRemoveDocumentModal] = useState({
        show: false,
        confirmation: ""
    });
    const [splitDocumentModal, setSplitDocumentModal] = useState({
        show: false,
        confirmation: ""
    });
    const [reconvertDocumentModal, setReconvertDocumentModal] = useState({
        show: false,
        confirmation: ""
    });
    const [signerPreviewModal, setSignerPreviewModal] = useState({
        show: false,
        loading: false,
        error: "",
        recipient: null,
        documents: [],
        fields: []
    });
    const [signatureEvents, setSignatureEvents] = useState([]);
    const [loadingActivity, setLoadingActivity] = useState(false);
    const [activityError, setActivityError] = useState("");
    const [signingLinks, setSigningLinks] = useState([]);
    const [loadingSigningLinks, setLoadingSigningLinks] = useState(false);
    const [signingLinksError, setSigningLinksError] = useState("");
    const [signingLinkAction, setSigningLinkAction] = useState("");
    const [copyFeedback, setCopyFeedback] = useState("");
    const [sendOutcome, setSendOutcome] = useState("");
    const [activeAuthoringPageNumber, setActiveAuthoringPageNumber] = useState(1);
    const [snapToGrid, setSnapToGrid] = useState(false);
    const [fieldClipboard, setFieldClipboard] = useState([]);
    const [fieldHistory, setFieldHistory] = useState({ undo: [], redo: [] });

    const documents = useMemo(() => envelope?.Documents || [], [envelope]);
    const recipients = useMemo(() => envelope?.Recipients || [], [envelope]);
    const fields = useMemo(() => envelope?.Fields || [], [envelope]);
    const signers = useMemo(() => recipients.filter((recipient) => recipient.recipientType === "signer"), [recipients]);
    const ccRecipients = useMemo(() => recipients.filter((recipient) => recipient.recipientType !== "signer"), [recipients]);
    const activeDocument = useMemo(() => (
        documents.find((document) => document.uuid === activeDocumentUuid) || documents[0] || null
    ), [activeDocumentUuid, documents]);
    const activeDocumentPageCount = useMemo(() => documentPageCount(activeDocument), [activeDocument]);
    const isDraft = envelope?.status === "draft";
    const isTemplateDraft = useMemo(() => envelopeIsTemplate(envelope), [envelope]);
    const orderedSigning = optionsForm.routingMode !== "parallel";
    const recipientsByUuid = useMemo(() => new Map(recipients.map((recipient) => [recipient.uuid, recipient])), [recipients]);
    const documentsByUuid = useMemo(() => new Map(documents.map((document) => [document.uuid, document])), [documents]);
    const previewFields = useMemo(() => (
        activeDocument
            ? fields.filter((field) => field.documentUuid === activeDocument.uuid)
            : []
    ), [activeDocument, fields]);
    const selectedField = useMemo(() => (
        fields.find((field) => field.uuid === selectedFieldUuid) || null
    ), [fields, selectedFieldUuid]);
    const selectedFields = useMemo(() => (
        fields.filter((field) => selectedFieldUuids.includes(field.uuid))
    ), [fields, selectedFieldUuids]);
    const selectedFieldNameWarning = useMemo(() => {
        if (!selectedField || selectedFieldUuids.length > 1) return "";
        const name = normalizeFieldName(selectedFieldForm.label);
        if (!name) return "";
        const duplicateCount = duplicateFieldNameCount(name, fields, {
            documentUuid: selectedField.documentUuid,
            ignoreUuids: [selectedField.uuid]
        });
        return duplicateCount > 0
            ? `Another field in this document uses "${name}". Matching names are allowed and will be grouped as an array in document metadata.`
            : "";
    }, [fields, selectedField, selectedFieldForm.label, selectedFieldUuids.length]);
    const selectedFieldApiNameWarning = useMemo(() => {
        if (!selectedField || selectedFieldUuids.length > 1) return "";
        const recipient = recipientsByUuid.get(selectedField.recipientUuid);
        const isContextRecipient = isDigitalSigningContext(recipient);
        const apiName = String(selectedFieldForm.apiName || "").trim().toLowerCase();
        if (!isContextRecipient && !apiName) return "";
        if (!apiName) return "API field name is required for fields assigned to a digital signing context.";
        if (!API_FIELD_NAME_PATTERN.test(apiName)) return "Use 1–128 lowercase letters, numbers, periods, underscores, or hyphens, starting with a letter or number.";
        const duplicate = fields.some((field) => (
            field.uuid !== selectedField.uuid &&
            normalizeFieldMetadata(field.metadata).apiName === apiName
        ));
        return duplicate ? `Another field uses the API field name "${apiName}".` : "";
    }, [fields, recipientsByUuid, selectedField, selectedFieldForm.apiName, selectedFieldUuids.length]);
    const documentOptions = useMemo(() => documents.map((document) => ({
        value: document.uuid,
        label: documentTitle(document)
    })), [documents]);
    const signerOptions = useMemo(() => signers.map((recipient) => ({
        value: recipient.uuid,
        label: `${recipient.name} (${recipient.email})`
    })), [signers]);
    const selectMenuTarget = selectMenuPortalTarget();
    const notifySuccess = useCallback((text) => {
        dispatch(sendAlert(text, "success"));
    }, [dispatch]);
    const updatePageInfo = useCallback((settings) => {
        dispatch(pageInfo(settings));
    }, [dispatch]);
    const focusFieldKeyboardTarget = useCallback(() => {
        window.requestAnimationFrame(() => {
            fieldKeyboardTargetRef.current?.focus({ preventScroll: true });
        });
    }, []);
    const applyFieldUpdates = useCallback((updates) => {
        const updatesByUuid = new Map(
            updates
                .filter((item) => item?.fieldUuid)
                .map((item) => [item.fieldUuid, item.values || {}])
        );
        if (updatesByUuid.size === 0) return;

        const applyToFields = (items = []) => items.map((field) => (
            updatesByUuid.has(field.uuid)
                ? { ...field, ...updatesByUuid.get(field.uuid) }
                : field
        ));

        fieldsRef.current = applyToFields(fieldsRef.current);
        setEnvelope((current) => {
            if (!current?.Fields) return current;
            return {
                ...current,
                Fields: applyToFields(current.Fields)
            };
        });
    }, []);


    const loadEnvelope = useCallback(async ({ showLoading = true } = {}) => {
        if (showLoading) {
            setLoading(true);
        }
        setError("");
        try {
            const response = await signApi.get(`/signature/envelopes/${uuid}`);
            const item = response.data?.item;
            if (!response.data?.success || !item) {
                throw new Error(response.data?.error || "Unable to load signature envelope.");
            }

            setEnvelope(item);
            const availableDocuments = item.Documents || [];
            const currentActiveDocumentUuid = activeDocumentUuidRef.current;
            const nextActiveDocumentUuid = availableDocuments.some((document) => document.uuid === currentActiveDocumentUuid)
                ? currentActiveDocumentUuid
                : (availableDocuments[0]?.uuid || "");
            activeDocumentUuidRef.current = nextActiveDocumentUuid;
            setActiveDocumentUuid(nextActiveDocumentUuid);
            if (!optionsInitializedRef.current) {
                setOptionsForm({
                    title: item.title || item.displayName || "",
                    subject: item.subject || item.displayName || "Signature request",
                    messageBody: item.messageBody || "Please review and sign this document.",
                    internalNotes: item.internalNotes || "",
                    externalNotes: item.externalNotes || "",
                    expirationAt: dateInputValue(item.expirationAt) || addDays(30),
                    defaultReminderFrequency: item.defaultReminderFrequency || "daily",
                    defaultReminderEndsAt: dateInputValue(item.defaultReminderEndsAt) || addDays(14),
                    routingMode: item.routingMode === "parallel" ? "parallel" : "sequential",
                    category: item.category || "",
                    practiceArea: item.practiceArea || "",
                    documentType: item.documentType || "",
                    tags: mergedListText(item.tags, item.labels)
                });
                if (item.status !== "draft") setBuilderStep("final");
                optionsInitializedRef.current = true;
            }
            setFieldForm((current) => {
                const firstSigner = item.Recipients?.find((recipient) => recipient.recipientType === "signer");
                const currentSignerStillExists = current.recipientUuid
                    && item.Recipients?.some((recipient) => recipient.uuid === current.recipientUuid && recipient.recipientType === "signer");
                const currentDocumentStillExists = current.documentUuid
                    && availableDocuments.some((document) => document.uuid === current.documentUuid);
                return {
                    ...current,
                    documentUuid: currentDocumentStillExists ? current.documentUuid : nextActiveDocumentUuid,
                    recipientUuid: currentSignerStillExists ? current.recipientUuid : (firstSigner?.uuid || "")
                };
            });
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to load signature envelope.");
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, [uuid]);

    const loadEnvelopeActivity = useCallback(async () => {
        setLoadingActivity(true);
        setActivityError("");
        try {
            const response = await signApi.get(`/signature/envelopes/${uuid}/events`);
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to load signature activity.");
            }
            setSignatureEvents(Array.isArray(response.data?.items) ? response.data.items : []);
        } catch (err) {
            setActivityError(err?.response?.data?.error || err.message || "Unable to load signature activity.");
        } finally {
            setLoadingActivity(false);
        }
    }, [uuid]);

    const loadSigningLinks = useCallback(async ({ showLoading = true } = {}) => {
        if (showLoading) setLoadingSigningLinks(true);
        setSigningLinksError("");
        try {
            const response = await signApi.get(`/signature/envelopes/${uuid}/signing-links`);
            if (response.data?.success === false) {
                throw new Error(response.data?.error?.message || response.data?.error || "Unable to load signing links.");
            }
            setSigningLinks(signingLinksFromResponse(response.data));
        } catch (err) {
            setSigningLinksError(apiErrorMessage(err, "Unable to load signing links."));
        } finally {
            if (showLoading) setLoadingSigningLinks(false);
        }
    }, [uuid]);

    const flushFieldUpdateQueue = useCallback(async () => {
        if (fieldPersistingRef.current) return;
        fieldPersistingRef.current = true;

        try {
            while (pendingFieldUpdatesRef.current.size > 0) {
                const batch = Array.from(pendingFieldUpdatesRef.current.values());
                pendingFieldUpdatesRef.current.clear();

                try {
                    await Promise.all(batch.map(({ fieldUuid, values }) => (
                        signApi.patch(`/signature/envelopes/${uuid}/fields/${fieldUuid}`, values)
                    )));
                } catch (err) {
                    const fallback = batch.find((item) => item.errorMessage)?.errorMessage || "Unable to update field.";
                    pendingFieldUpdatesRef.current.clear();
                    setError(err?.response?.data?.error || err.message || fallback);
                    await loadEnvelope({ showLoading: false });
                    break;
                }
            }
        } finally {
            fieldPersistingRef.current = false;
        }
    }, [loadEnvelope, uuid]);

    const persistFieldUpdates = useCallback((updates, { errorMessage = "Unable to update field.", successMessage = "" } = {}) => {
        const normalizedUpdates = updates
            .filter((item) => item?.fieldUuid)
            .map((item) => ({
                fieldUuid: item.fieldUuid,
                values: normalizeFieldPayload(item.values || {})
            }));

        if (normalizedUpdates.length === 0) return;

        applyFieldUpdates(normalizedUpdates);
        setError("");

        normalizedUpdates.forEach(({ fieldUuid, values }) => {
            const previous = pendingFieldUpdatesRef.current.get(fieldUuid);
            pendingFieldUpdatesRef.current.set(fieldUuid, {
                fieldUuid,
                values: {
                    ...(previous?.values || {}),
                    ...values
                },
                errorMessage
            });
        });

        if (successMessage) notifySuccess(successMessage);
        flushFieldUpdateQueue();
    }, [applyFieldUpdates, flushFieldUpdateQueue, notifySuccess]);

    const pushFieldUpdateHistory = useCallback((updates) => {
        const updatesByUuid = new Map(
            updates
                .filter((item) => item?.fieldUuid)
                .map((item) => [item.fieldUuid, item.values || {}])
        );
        if (updatesByUuid.size === 0) return;

        const before = [];
        const after = [];
        fieldsRef.current.forEach((field) => {
            const values = updatesByUuid.get(field.uuid);
            if (!values) return;
            before.push({ fieldUuid: field.uuid, values: fieldPatchSnapshot(field) });
            after.push({
                fieldUuid: field.uuid,
                values: fieldPatchSnapshot({
                    ...field,
                    ...values,
                    metadata: values.metadata ?? field.metadata
                })
            });
        });

        if (before.length === 0 || after.length === 0) return;

        setFieldHistory((current) => ({
            undo: [...current.undo, { before, after }].slice(-FIELD_HISTORY_LIMIT),
            redo: []
        }));
    }, []);

    const persistFieldUpdatesWithHistory = useCallback((updates, options = {}) => {
        if (options.recordHistory !== false) {
            pushFieldUpdateHistory(updates);
        }
        persistFieldUpdates(updates, options);
    }, [persistFieldUpdates, pushFieldUpdateHistory]);

    const undoLastFieldChange = () => {
        const entry = fieldHistory.undo[fieldHistory.undo.length - 1];
        if (!entry || saving) return;
        setFieldHistory((current) => ({
            undo: current.undo.slice(0, -1),
            redo: [...current.redo, entry].slice(-FIELD_HISTORY_LIMIT)
        }));
        persistFieldUpdates(entry.before, {
            errorMessage: "Unable to undo field change."
        });
        focusFieldKeyboardTarget();
    };

    const redoLastFieldChange = () => {
        const entry = fieldHistory.redo[fieldHistory.redo.length - 1];
        if (!entry || saving) return;
        setFieldHistory((current) => ({
            undo: [...current.undo, entry].slice(-FIELD_HISTORY_LIMIT),
            redo: current.redo.slice(0, -1)
        }));
        persistFieldUpdates(entry.after, {
            errorMessage: "Unable to redo field change."
        });
        focusFieldKeyboardTarget();
    };

    useEffect(() => {
        optionsInitializedRef.current = false;
        loadEnvelope();
    }, [loadEnvelope]);

    useEffect(() => {
        loadEnvelopeActivity();
    }, [loadEnvelopeActivity]);

    useEffect(() => {
        if (envelope?.status && envelope.status !== "draft") loadSigningLinks();
    }, [envelope?.status, loadSigningLinks]);

    useEffect(() => () => {
        if (copyFeedbackTimerRef.current) window.clearTimeout(copyFeedbackTimerRef.current);
    }, []);

    useEffect(() => {
        fieldsRef.current = fields;
    }, [fields]);

    useEffect(() => {
        activeDocumentUuidRef.current = activeDocumentUuid;
    }, [activeDocumentUuid]);

    useEffect(() => {
        setActiveAuthoringPageNumber((current) => clamp(Math.max(1, Math.round(Number(current) || 1)), 1, Math.max(1, activeDocumentPageCount || 1)));
        selectedFieldUuidsRef.current = [];
        setSelectedFieldUuid("");
        setSelectedFieldUuids([]);
    }, [activeDocumentUuid, activeDocumentPageCount]);

    useEffect(() => {
        selectedFieldUuidsRef.current = selectedFieldUuids;
    }, [selectedFieldUuids]);

    useEffect(() => {
        const availableUuids = new Set(fields.map((field) => field.uuid));
        setSelectedFieldUuids((current) => {
            const next = current.filter((fieldUuid) => availableUuids.has(fieldUuid));
            return next.length === current.length ? current : next;
        });
        if (selectedFieldUuid && !availableUuids.has(selectedFieldUuid)) {
            const fallback = selectedFieldUuids.find((fieldUuid) => availableUuids.has(fieldUuid)) || "";
            setSelectedFieldUuid(fallback);
        }
    }, [fields, selectedFieldUuid, selectedFieldUuids]);

    useEffect(() => {
        if (!selectedField) {
            selectedFieldFormBaselineRef.current = EMPTY_SELECTED_FIELD;
            selectedFieldFormSnapshotRef.current = "";
            setSelectedFieldForm(EMPTY_SELECTED_FIELD);
            return;
        }

        const metadata = normalizeFieldMetadata(selectedField.metadata);
        const nextForm = {
            recipientUuid: selectedField.recipientUuid || "",
            fieldType: selectedField.fieldType || "signature",
            pageNumber: selectedField.pageNumber || 1,
            x: fieldDecimal(selectedField.x, 0),
            y: fieldDecimal(selectedField.y, 0),
            width: fieldDecimal(selectedField.width, 0.26),
            height: fieldDecimal(selectedField.height, 0.05),
            required: selectedField.required !== false,
            label: fieldEffectiveName(selectedField),
            apiName: metadata.apiName || metadata.templateField?.name || "",
            validation: fieldValidationText(selectedField.validation),
            textAlign: metadata.textAlign,
            fontFamily: metadata.fontFamily,
            fontSize: metadata.fontSize
        };
        selectedFieldFormBaselineRef.current = nextForm;
        selectedFieldFormSnapshotRef.current = selectedFields.length > 1
            ? selectedFieldUpdateSignature([])
            : selectedFieldUpdateSignature(selectedFieldUpdateItemsFromForm(nextForm, selectedField, [selectedField]));
        setSelectedFieldForm(nextForm);
        if (selectedField.documentUuid === activeDocument?.uuid) {
            setActiveAuthoringPageNumber(Math.max(1, Math.round(Number(selectedField.pageNumber || 1))));
        }
    }, [activeDocument?.uuid, selectedField, selectedFields]);

    useEffect(() => {
        if (!isDraft || !selectedField) return undefined;
        const multipleSelected = selectedFields.length > 1;
        const changedFields = multipleSelected
            ? selectedFieldFormChangedKeys(selectedFieldForm, selectedFieldFormBaselineRef.current, MULTI_SELECTED_FIELD_SYNC_FIELDS)
            : null;
        const updateItems = selectedFieldUpdateItemsFromForm(
            selectedFieldForm,
            selectedField,
            multipleSelected ? selectedFields : [selectedField],
            { changedFields }
        );
        if (updateItems.length === 0) return undefined;
        const signature = selectedFieldUpdateSignature(updateItems);
        if (signature === selectedFieldFormSnapshotRef.current) return undefined;

        if (selectedFieldAutoSaveTimerRef.current) {
            window.clearTimeout(selectedFieldAutoSaveTimerRef.current);
        }
        selectedFieldAutoSaveTimerRef.current = window.setTimeout(() => {
            selectedFieldFormSnapshotRef.current = signature;
            persistFieldUpdatesWithHistory(updateItems, {
                errorMessage: updateItems.length > 1 ? "Unable to update selected fields." : "Unable to update field."
            });
            selectedFieldAutoSaveTimerRef.current = null;
        }, 450);

        return () => {
            if (selectedFieldAutoSaveTimerRef.current) {
                window.clearTimeout(selectedFieldAutoSaveTimerRef.current);
                selectedFieldAutoSaveTimerRef.current = null;
            }
        };
    }, [isDraft, persistFieldUpdatesWithHistory, selectedField, selectedFieldForm, selectedFields]);

    useEffect(() => {
        const title = activeDocument ? `Signature Request (${documentTitle(activeDocument)})` : "Signature Request";
        updatePageInfo({
            title,
            breadcrumb: title
        });
    }, [activeDocument, updatePageInfo]);

    const selectField = (fieldUuid, { additive = false, focusEditor = false } = {}) => {
        if (!fieldUuid) {
            selectedFieldUuidsRef.current = [];
            setSelectedFieldUuid("");
            setSelectedFieldUuids([]);
            if (focusEditor) focusFieldKeyboardTarget();
            return;
        }

        if (!additive) {
            selectedFieldUuidsRef.current = [fieldUuid];
            setSelectedFieldUuid(fieldUuid);
            setSelectedFieldUuids([fieldUuid]);
            if (focusEditor) focusFieldKeyboardTarget();
            return;
        }

        setSelectedFieldUuids((current) => {
            const exists = current.includes(fieldUuid);
            const next = exists
                ? current.filter((item) => item !== fieldUuid)
                : [...current, fieldUuid];
            selectedFieldUuidsRef.current = next;
            setSelectedFieldUuid(exists ? (next[next.length - 1] || "") : fieldUuid);
            if (focusEditor) focusFieldKeyboardTarget();
            return next;
        });
    };

    const selectFields = (fieldUuids, { additive = false, focusEditor = false } = {}) => {
        const unique = [...new Set(fieldUuids)].filter(Boolean);
        if (!additive) {
            selectedFieldUuidsRef.current = unique;
            setSelectedFieldUuids(unique);
            setSelectedFieldUuid(unique[unique.length - 1] || "");
            if (focusEditor) focusFieldKeyboardTarget();
            return;
        }

        setSelectedFieldUuids((current) => {
            const next = [...current];
            unique.forEach((fieldUuid) => {
                if (!next.includes(fieldUuid)) {
                    next.push(fieldUuid);
                }
            });
            selectedFieldUuidsRef.current = next;
            setSelectedFieldUuid(unique[unique.length - 1] || next[next.length - 1] || "");
            if (focusEditor) focusFieldKeyboardTarget();
            return next;
        });
    };

    const updateRecipientRow = (recipientType, rowId, field, value) => {
        const setter = recipientType === "signer" ? setSignerRows : setCcRows;
        setter((rows) => rows.map((row) => (
            row.id === rowId ? { ...row, [field]: value } : row
        )));
    };

    const applyPersonToRecipientRow = (recipientType, rowId, person) => {
        const setter = recipientType === "signer" ? setSignerRows : setCcRows;
        setter((rows) => rows.map((row) => (
            row.id === rowId
                ? { ...row, name: personDisplayName(person), email: personEmail(person) }
                : row
        )));
    };

    const addRecipientRow = (recipientType) => {
        const setter = recipientType === "signer" ? setSignerRows : setCcRows;
        setter((rows) => [...rows, newRecipientRow()]);
    };

    const removeRecipientRow = (recipientType, rowId) => {
        const setter = recipientType === "signer" ? setSignerRows : setCcRows;
        setter((rows) => rows.filter((row) => row.id !== rowId));
    };

    const updateFieldForm = (field, value) => {
        setFieldForm((current) => ({ ...current, [field]: value }));
    };

    const updateSelectedFieldForm = (field, value) => {
        setSelectedFieldForm((current) => ({ ...current, [field]: value }));
    };

    const updateOptionsForm = (field, value) => {
        setOptionsForm((current) => ({ ...current, [field]: value }));
    };

    const updateSigningOrderMode = async (enabled) => {
        const routingMode = enabled ? "sequential" : "parallel";
        setOptionsForm((current) => ({ ...current, routingMode }));
        if (!isDraft) return;

        setSaving(true);
        setError("");
        try {
            if (signers.length > 0) {
                await signApi.patch(`/signature/envelopes/${uuid}/recipients/order`, {
                    recipientType: "signer",
                    routingMode,
                    recipients: signers.map((item, index) => ({
                        recipientUuid: item.uuid,
                        routingOrder: index + 1
                    }))
                });
            } else {
                await signApi.patch(`/signature/envelopes/${uuid}`, {
                    routingMode
                });
            }
            await loadEnvelope({ showLoading: false });
            notifySuccess(enabled ? "Signing order enabled." : "Signing order disabled.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to update signing order.");
        } finally {
            setSaving(false);
        }
    };

    const saveEnvelopeOptions = async () => {
        const payload = {
            title: String(optionsForm.title || "").trim() || null,
            displayName: String(optionsForm.title || "").trim() || null,
            subject: String(optionsForm.subject || "").trim() || "Signature request",
            messageBody: optionsForm.messageBody,
            expirationAt: optionsForm.expirationAt ? new Date(`${optionsForm.expirationAt}T23:59:59`).toISOString() : null,
            defaultReminderFrequency: optionsForm.defaultReminderFrequency,
            defaultReminderEndsAt: optionsForm.defaultReminderEndsAt ? new Date(`${optionsForm.defaultReminderEndsAt}T23:59:59`).toISOString() : null,
            routingMode: optionsForm.routingMode,
            tags: optionsForm.tags
        };

        if (SIGNATURE_METADATA_FIELDS_ENABLED) {
            Object.assign(payload, {
                internalNotes: optionsForm.internalNotes,
                externalNotes: optionsForm.externalNotes,
                category: optionsForm.category,
                practiceArea: optionsForm.practiceArea,
                documentType: optionsForm.documentType
            });
        }

        await signApi.patch(`/signature/envelopes/${uuid}`, payload);
    };

    const saveRecipientRows = async (recipientType, { nextStep = null, successMessage = "" } = {}) => {
        const rows = recipientType === "signer" ? signerRows : ccRows;
        const existingCount = recipientType === "signer" ? signers.length : ccRecipients.length;
        const { complete, partial } = splitRecipientRows(rows);

        if (partial.length > 0) {
            setError("Every recipient row with a name or email needs both full name and email.");
            return false;
        }

        if (recipientType === "signer" && existingCount + complete.length === 0) {
            setError("Add at least one signing recipient before setting up fields.");
            return false;
        }

        if (recipientType === "signer") {
            setSaving(true);
            setError("");
            try {
                if (signers.length > 0) {
                    await signApi.patch(`/signature/envelopes/${uuid}/recipients/order`, {
                        recipientType: "signer",
                        routingMode: optionsForm.routingMode,
                        recipients: signers.map((item, index) => ({
                            recipientUuid: item.uuid,
                            routingOrder: index + 1
                        }))
                    });
                } else {
                    await signApi.patch(`/signature/envelopes/${uuid}`, {
                        routingMode: optionsForm.routingMode
                    });
                }
            } catch (err) {
                setSaving(false);
                setError(err?.response?.data?.error || err.message || "Unable to save routing mode.");
                return false;
            }
        }

        if (complete.length === 0) {
            if (recipientType === "signer") {
                setSaving(false);
            }
            if (nextStep) {
                setBuilderStep(nextStep);
            }
            return true;
        }

        setSaving(true);
        setError("");

        try {
            for (const [index, row] of complete.entries()) {
                await signApi.post(`/signature/envelopes/${uuid}/recipients`, {
                    name: row.name,
                    email: row.email,
                    recipientType,
                    isRequired: recipientType === "signer" ? row.isRequired !== false : false,
                    routingOrder: recipientType === "signer" && optionsForm.routingMode !== "parallel" ? existingCount + index + 1 : null
                });
            }

            if (recipientType === "signer") {
                setSignerRows(initialRecipientRows());
            } else {
                setCcRows(initialRecipientRows());
            }

            await loadEnvelope({ showLoading: false });
            if (nextStep) {
                setBuilderStep(nextStep);
            }
            if (successMessage) {
                notifySuccess(successMessage);
            }
            return true;
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to save recipients.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const deleteRecipient = async (recipientUuid) => {
        if (!recipientUuid) return;
        setSaving(true);
        setError("");

        try {
            const response = await signApi.delete(`/signature/envelopes/${uuid}/recipients/${recipientUuid}`);
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to delete recipient.");
            }
            await loadEnvelope({ showLoading: false });
            notifySuccess("Recipient deleted.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to delete recipient.");
        } finally {
            setSaving(false);
        }
    };

    const updateSavedRecipient = async (recipient, values, successMessage = "Recipient updated.") => {
        if (!recipient?.uuid || !isDraft) return;
        setSaving(true);
        setError("");

        try {
            const response = await signApi.patch(`/signature/envelopes/${uuid}/recipients/${recipient.uuid}`, values);
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to update recipient.");
            }
            await loadEnvelope({ showLoading: false });
            notifySuccess(successMessage);
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to update recipient.");
        } finally {
            setSaving(false);
        }
    };

    const persistRecipientOrder = async (recipientType, nextItems) => {
        setSaving(true);
        setError("");

        try {
            const response = await signApi.patch(`/signature/envelopes/${uuid}/recipients/order`, {
                recipientType,
                routingMode: recipientType === "signer" ? optionsForm.routingMode : undefined,
                recipients: nextItems.map((item, index) => ({
                    recipientUuid: item.uuid,
                    routingOrder: index + 1
                }))
            });
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to reorder recipients.");
            }
            await loadEnvelope({ showLoading: false });
            notifySuccess(recipientType === "signer" ? "Signer order updated." : "Recipient order updated.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to reorder recipients.");
        } finally {
            setSaving(false);
        }
    };

    const reorderRecipient = async (recipient, direction, recipientType) => {
        if (!recipient?.uuid || !isDraft) return;
        if (recipientType === "signer" && !orderedSigning) return;
        const items = recipientType === "signer" ? signers : ccRecipients;
        const currentIndex = items.findIndex((item) => item.uuid === recipient.uuid);
        const targetIndex = currentIndex + direction;
        if (currentIndex < 0 || targetIndex < 0 || targetIndex >= items.length) return;

        const nextItems = [...items];
        [nextItems[currentIndex], nextItems[targetIndex]] = [nextItems[targetIndex], nextItems[currentIndex]];

        await persistRecipientOrder(recipientType, nextItems);
    };

    const handleRecipientDragStart = (event, recipient, recipientType) => {
        if (!isDraft || saving || !recipient?.uuid) return;
        if (recipientType === "signer" && !orderedSigning) return;
        recipientDragRef.current = { uuid: recipient.uuid, recipientType };
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", recipient.uuid);
    };

    const handleRecipientDragOver = (event) => {
        if (!recipientDragRef.current) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    const handleRecipientDrop = async (event, targetRecipient, recipientType) => {
        event.preventDefault();
        const active = recipientDragRef.current;
        recipientDragRef.current = null;
        if (recipientType === "signer" && !orderedSigning) return;
        if (!active || active.recipientType !== recipientType || active.uuid === targetRecipient?.uuid) return;
        const items = recipientType === "signer" ? signers : ccRecipients;
        const currentIndex = items.findIndex((item) => item.uuid === active.uuid);
        const targetIndex = items.findIndex((item) => item.uuid === targetRecipient?.uuid);
        if (currentIndex < 0 || targetIndex < 0) return;

        const nextItems = [...items];
        const [moved] = nextItems.splice(currentIndex, 1);
        nextItems.splice(targetIndex, 0, moved);
        await persistRecipientOrder(recipientType, nextItems);
    };

    const handleRecipientDragEnd = () => {
        recipientDragRef.current = null;
    };

    const closeSignerPreviewModal = () => {
        setSignerPreviewModal({
            show: false,
            loading: false,
            error: "",
            recipient: null,
            documents: [],
            fields: []
        });
    };

    const previewSignerExperience = async (recipient) => {
        if (!recipient?.uuid) return;

        setSignerPreviewModal({
            show: true,
            loading: true,
            error: "",
            recipient,
            documents: [],
            fields: []
        });

        try {
            const response = await signApi.get(`/signature/envelopes/${uuid}/recipients/${recipient.uuid}/preview`);
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to preview signer experience.");
            }

            const item = response.data?.item || {};
            setSignerPreviewModal({
                show: true,
                loading: false,
                error: "",
                recipient: item.recipient || recipient,
                documents: item.documents || [],
                fields: item.fields || []
            });
        } catch (err) {
            setSignerPreviewModal((current) => ({
                ...current,
                loading: false,
                error: err?.response?.data?.error || err.message || "Unable to preview signer experience."
            }));
        }
    };

    const addField = async (override = {}) => {
        const payload = {
            ...fieldForm,
            documentUuid: activeDocument?.uuid || fieldForm.documentUuid,
            ...override
        };
        if (!payload.documentUuid || !payload.recipientUuid) {
            setError("Choose a signer before adding a field.");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const label = uniqueFieldName(payload.label || fieldTypeLabel(payload.fieldType), fieldsRef.current, {
                documentUuid: payload.documentUuid
            });
            const response = await signApi.post(`/signature/envelopes/${uuid}/fields`, normalizeFieldPayload({
                ...payload,
                label,
                metadata: fieldMetadataPayload({
                    ...normalizeFieldMetadata(payload.metadata || DEFAULT_FIELD_METADATA),
                    name: label
                })
            }));
            const newFieldUuid = response.data?.item?.uuid || "";
            await loadEnvelope({ showLoading: false });
            if (newFieldUuid) {
                selectField(newFieldUuid);
            }
            notifySuccess("Field added.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to add field.");
        } finally {
            setSaving(false);
        }
    };

    const addStandardField = (fieldType, placement = {}) => {
        const definition = standardFieldDefinition(fieldType);
        const placementValues = clampFieldPlacement(snapToGrid
            ? snapFieldPlacement({
                pageNumber: placement.pageNumber || activeAuthoringPageNumber,
                x: placement.x ?? fieldForm.x,
                y: placement.y ?? fieldForm.y,
                width: placement.width ?? definition.width,
                height: placement.height ?? definition.height
            })
            : {
                pageNumber: placement.pageNumber || activeAuthoringPageNumber,
                x: placement.x ?? fieldForm.x,
                y: placement.y ?? fieldForm.y,
                width: placement.width ?? definition.width,
                height: placement.height ?? definition.height
            });
        return addField({
            fieldType,
            label: definition.label,
            ...placement,
            ...placementValues,
            metadata: DEFAULT_FIELD_METADATA
        });
    };

    const deleteField = async (fieldUuid) => {
        if (!fieldUuid) return;
        setSaving(true);
        setError("");

        try {
            const response = await signApi.delete(`/signature/envelopes/${uuid}/fields/${fieldUuid}`);
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to delete field.");
            }
            if (selectedFieldUuid === fieldUuid) {
                setSelectedFieldUuid("");
            }
            setSelectedFieldUuids((current) => current.filter((uuid) => uuid !== fieldUuid));
            await loadEnvelope({ showLoading: false });
            notifySuccess("Field deleted.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to delete field.");
        } finally {
            setSaving(false);
        }
    };

    const selectDocument = (documentUuid) => {
        const nextUuid = documentUuid || documents[0]?.uuid || "";
        activeDocumentUuidRef.current = nextUuid;
        setActiveDocumentUuid(nextUuid);
        setFieldForm((current) => ({ ...current, documentUuid: nextUuid }));
        selectField("");
    };

    const requestAddDocument = () => {
        if (!isDraft) return;
        addFileInputRef.current?.click();
    };

    const addDocumentFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("displayName", file.name);

        setSaving(true);
        setError("");

        try {
            const response = await signApi.post(`/signature/envelopes/${uuid}/documents`, formData);
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to add document.");
            }
            const documentUuid = response.data?.item?.uuid || "";
            if (documentUuid) {
                activeDocumentUuidRef.current = documentUuid;
                setActiveDocumentUuid(documentUuid);
            }
            await loadEnvelope({ showLoading: false });
            notifySuccess("Document added.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to add document.");
        } finally {
            setSaving(false);
        }
    };

    const reorderActiveDocument = async (direction) => {
        if (!isDraft || !activeDocument?.uuid || documents.length < 2) return;
        const currentIndex = documents.findIndex((document) => document.uuid === activeDocument.uuid);
        const targetIndex = currentIndex + direction;
        if (currentIndex < 0 || targetIndex < 0 || targetIndex >= documents.length) return;

        const nextDocuments = [...documents];
        [nextDocuments[currentIndex], nextDocuments[targetIndex]] = [nextDocuments[targetIndex], nextDocuments[currentIndex]];

        setSaving(true);
        setError("");

        try {
            const response = await signApi.patch(`/signature/envelopes/${uuid}/documents/order`, {
                documents: nextDocuments.map((document) => document.uuid)
            });
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to reorder documents.");
            }
            await loadEnvelope({ showLoading: false });
            notifySuccess("Document order updated.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to reorder documents.");
        } finally {
            setSaving(false);
        }
    };

    const requestRemoveActiveDocument = () => {
        if (!isDraft || !activeDocument?.uuid) return;
        setRemoveDocumentModal({
            show: true,
            confirmation: ""
        });
    };

    const closeRemoveDocumentModal = () => {
        setRemoveDocumentModal({
            show: false,
            confirmation: ""
        });
    };

    const confirmRemoveActiveDocument = async () => {
        if (removeDocumentModal.confirmation !== "REMOVE" || !activeDocument?.uuid) return;
        const removedDocumentUuid = activeDocument.uuid;

        closeRemoveDocumentModal();
        setSaving(true);
        setError("");

        try {
            const response = await signApi.delete(`/signature/envelopes/${uuid}/documents/${removedDocumentUuid}`);
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to remove document.");
            }
            const nextDocument = documents.find((document) => document.uuid !== removedDocumentUuid);
            activeDocumentUuidRef.current = nextDocument?.uuid || "";
            setActiveDocumentUuid(nextDocument?.uuid || "");
            selectField("");
            await loadEnvelope({ showLoading: false });
            notifySuccess("Document removed.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to remove document.");
        } finally {
            setSaving(false);
        }
    };

    const downloadSignatureContent = async (path, filename, successMessage) => {
        setSaving(true);
        setError("");

        try {
            const response = await signApi.get(path, { responseType: "blob" });
            downloadBlob(response.data, filename);
            notifySuccess(successMessage || "Document downloaded.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to download document.");
        } finally {
            setSaving(false);
        }
    };

    const downloadDocumentPdf = (document, kind) => {
        if (!document?.uuid) return;
        const endpoints = {
            original: "original-content",
            working: "content",
            signed: "signed-content",
            completed: "completed-content",
            audit: "audit-certificate"
        };
        const endpoint = endpoints[kind] || endpoints.working;
        const labels = {
            original: "original",
            working: "working",
            signed: "signed",
            completed: "completed",
            audit: "audit certificate"
        };

        downloadSignatureContent(
            `/signature/envelopes/${uuid}/documents/${document.uuid}/${endpoint}`,
            kind === "original" ? originalDownloadFilename(document) : filenameWithSuffix(document, labels[kind] || "document"),
            `${labels[kind] || "Document"} downloaded.`
        );
    };

    const downloadEnvelopePacket = (kind = "signed") => {
        downloadSignatureContent(
            `/signature/envelopes/${uuid}/packet-content?kind=${encodeURIComponent(kind)}`,
            filenameWithSuffix({ displayName: envelope?.displayName || envelope?.subject || "Signature Packet" }, `${kind} packet`),
            "Packet downloaded."
        );
    };

    const requestReplaceDocument = () => {
        if (!isDraft || !activeDocument?.uuid) return;
        replaceFileInputRef.current?.click();
    };

    const replaceActiveDocumentFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file || !activeDocument?.uuid) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("displayName", file.name);

        setSaving(true);
        setError("");

        try {
            const response = await signApi.put(`/signature/envelopes/${uuid}/documents/${activeDocument.uuid}`, formData);
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to replace document.");
            }
            await loadEnvelope({ showLoading: false });
            notifySuccess("Document replaced.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to replace document.");
        } finally {
            setSaving(false);
        }
    };

    const requestSplitActiveDocument = () => {
        if (!isDraft || !activeDocument?.uuid) return;
        setSplitDocumentModal({
            show: true,
            confirmation: ""
        });
    };

    const closeSplitDocumentModal = () => {
        setSplitDocumentModal({
            show: false,
            confirmation: ""
        });
    };

    const requestReconvertActiveDocument = () => {
        if (!isDraft || !activeDocument?.uuid || !documentIsDocxSource(activeDocument)) return;
        setReconvertDocumentModal({
            show: true,
            confirmation: ""
        });
    };

    const closeReconvertDocumentModal = () => {
        setReconvertDocumentModal({
            show: false,
            confirmation: ""
        });
    };

    const confirmReconvertActiveDocument = async () => {
        if (reconvertDocumentModal.confirmation !== "RECONVERT" || !activeDocument?.uuid) return;

        closeReconvertDocumentModal();
        setSaving(true);
        setError("");

        try {
            const response = await signApi.post(`/signature/envelopes/${uuid}/documents/${activeDocument.uuid}/reconvert`);
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to re-convert document.");
            }

            await loadEnvelope({ showLoading: false });
            notifySuccess(response.data?.geometryChanged
                ? "Document re-converted. Field placements were cleared because page geometry changed."
                : "Document re-converted.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to re-convert document.");
        } finally {
            setSaving(false);
        }
    };

    const confirmSplitActiveDocument = async () => {
        if (splitDocumentModal.confirmation !== "SPLIT" || !activeDocument?.uuid) return;
        const pageCount = documentPageCount(activeDocument);
        if (pageCount < 2) {
            setError("The selected document must have at least two pages to split.");
            return;
        }

        const baseName = documentTitle(activeDocument).replace(/\.[^.]+$/, "").trim() || "Document";
        const ranges = Array.from({ length: pageCount }, (_, index) => ({
            from: index + 1,
            to: index + 1,
            displayName: `${baseName} page ${index + 1}.pdf`
        }));

        closeSplitDocumentModal();
        setSaving(true);
        setError("");

        try {
            const response = await signApi.post(`/signature/envelopes/${uuid}/documents/${activeDocument.uuid}/split`, { ranges });
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to split document.");
            }
            await loadEnvelope({ showLoading: false });
            notifySuccess("Document split into pages.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to split document.");
        } finally {
            setSaving(false);
        }
    };

    const alignSelectedFields = (alignment) => {
        if (selectedFields.length < 2) return;

        const geometries = selectedFields.map((field) => ({
            field,
            ...fieldGeometry(field)
        }));
        const left = Math.min(...geometries.map((item) => item.x));
        const right = Math.max(...geometries.map((item) => item.x + item.width));
        const center = left + ((right - left) / 2);
        const top = Math.min(...geometries.map((item) => item.y));
        const bottom = Math.max(...geometries.map((item) => item.y + item.height));
        const middle = top + ((bottom - top) / 2);

        const updates = geometries.map((item) => {
            let x = item.x;
            let y = item.y;
            if (alignment === "left") {
                x = left;
            } else if (alignment === "center") {
                x = center - (item.width / 2);
            } else if (alignment === "right") {
                x = right - item.width;
            } else if (alignment === "top") {
                y = top;
            } else if (alignment === "middle") {
                y = middle - (item.height / 2);
            } else if (alignment === "bottom") {
                y = bottom - item.height;
            }

            return {
                fieldUuid: item.field.uuid,
                values: {
                    x: clamp(x, 0, Math.max(0, 1 - item.width)),
                    y: clamp(y, 0, Math.max(0, 1 - item.height))
                }
            };
        });

        persistFieldUpdatesWithHistory(updates, {
            errorMessage: "Unable to align fields.",
            successMessage: "Fields aligned."
        });
        focusFieldKeyboardTarget();
    };

    const nudgeSelectedFields = useCallback((direction, { large = false } = {}) => {
        if (!isDraft || saving) return;

        const selectedUuids = new Set(selectedFieldUuidsRef.current);
        const currentSelectedFields = fieldsRef.current.filter((field) => selectedUuids.has(field.uuid));
        if (currentSelectedFields.length === 0) return;
        const step = large ? FIELD_NUDGE_FAST_STEP : FIELD_NUDGE_STEP;
        const updates = currentSelectedFields
            .map((field) => {
                const geometry = fieldGeometry(field);
                const placement = clampFieldPlacement({
                    x: clamp(geometry.x + (direction.x * step), 0, Math.max(0, 1 - geometry.width)),
                    y: clamp(geometry.y + (direction.y * step), 0, Math.max(0, 1 - geometry.height))
                });

                if (placement.x === geometry.x && placement.y === geometry.y) return null;
                return { fieldUuid: field.uuid, values: placement };
            })
            .filter(Boolean);

        if (updates.length === 0) return;

        persistFieldUpdatesWithHistory(updates, {
            errorMessage: "Unable to move selected fields."
        });
    }, [isDraft, persistFieldUpdatesWithHistory, saving]);

    const resizeSelectedFieldsFromKeyboard = useCallback((direction) => {
        if (!isDraft || saving) return;

        const selectedUuids = new Set(selectedFieldUuidsRef.current);
        const currentSelectedFields = fieldsRef.current.filter((field) => selectedUuids.has(field.uuid));
        if (currentSelectedFields.length === 0) return;

        const updates = currentSelectedFields
            .map((field) => {
                const geometry = fieldGeometry(field);
                const values = {};
                if (direction.x < 0) {
                    values.width = clamp(geometry.width - FIELD_RESIZE_KEY_STEP, MIN_FIELD_SIZE, 1 - geometry.x);
                } else if (direction.x > 0) {
                    values.width = clamp(geometry.width + FIELD_RESIZE_KEY_STEP, MIN_FIELD_SIZE, 1 - geometry.x);
                } else if (direction.y < 0) {
                    values.height = clamp(geometry.height - FIELD_RESIZE_KEY_STEP, MIN_FIELD_SIZE, 1 - geometry.y);
                } else if (direction.y > 0) {
                    values.height = clamp(geometry.height + FIELD_RESIZE_KEY_STEP, MIN_FIELD_SIZE, 1 - geometry.y);
                }

                const placement = clampFieldPlacement(values);
                if (
                    (!Object.prototype.hasOwnProperty.call(placement, "width") || placement.width === geometry.width) &&
                    (!Object.prototype.hasOwnProperty.call(placement, "height") || placement.height === geometry.height)
                ) return null;

                return { fieldUuid: field.uuid, values: placement };
            })
            .filter(Boolean);

        if (updates.length === 0) return;

        persistFieldUpdatesWithHistory(updates, {
            errorMessage: "Unable to resize selected fields."
        });
    }, [isDraft, persistFieldUpdatesWithHistory, saving]);

    useEffect(() => {
        if (builderStep !== "fields" || selectedFieldUuids.length === 0) return undefined;

        const handleKeyDown = (event) => {
            const direction = FIELD_NUDGE_DELTAS[event.key];
            if (!direction) return;
            if (shouldIgnoreFieldNudgeTarget(event.target)) return;

            event.preventDefault();
            if (event.shiftKey) {
                resizeSelectedFieldsFromKeyboard(direction);
                return;
            }

            nudgeSelectedFields(direction, { large: event.ctrlKey });
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [builderStep, nudgeSelectedFields, resizeSelectedFieldsFromKeyboard, selectedFieldUuids.length]);

    const handlePaletteDragStart = (event, fieldType) => {
        if (!isDraft || !activeDocument?.uuid || !fieldForm.recipientUuid) {
            event.preventDefault();
            setError("Choose a signer before placing a field.");
            return;
        }

        event.dataTransfer.effectAllowed = "copy";
        event.dataTransfer.setData("application/x-signature-field", fieldType);
    };

    const dropNewField = (placement) => {
        addStandardField(placement.fieldType, placement);
    };

    const moveField = (fieldUuid, placement) => {
        selectField(fieldUuid);
        const nextPlacement = clampFieldPlacement(snapToGrid ? snapFieldPlacement({
            ...fieldGeometry(fieldsRef.current.find((field) => field.uuid === fieldUuid) || {}),
            ...placement
        }) : placement);
        return persistFieldUpdatesWithHistory([{
            fieldUuid,
            values: nextPlacement
        }], {
            errorMessage: "Unable to move field."
        });
    };

    const createFieldsFromPayloads = async (payloads, successMessage) => {
        const nextNamedFields = [];
        const filteredPayloads = payloads.filter(Boolean).map((payload) => {
            const documentUuid = payload.documentUuid || activeDocument?.uuid || "";
            const label = uniqueFieldName(payload.label || fieldTypeLabel(payload.fieldType), [...fieldsRef.current, ...nextNamedFields], {
                documentUuid
            });
            const namedPayload = {
                ...payload,
                documentUuid,
                label,
                metadata: fieldMetadataPayload({
                    ...normalizeFieldMetadata(payload.metadata || DEFAULT_FIELD_METADATA),
                    name: label
                })
            };
            nextNamedFields.push(namedPayload);
            return namedPayload;
        });
        if (filteredPayloads.length === 0) {
            setError("No new fields were needed.");
            return [];
        }

        setSaving(true);
        setError("");
        try {
            const responses = await Promise.all(filteredPayloads.map((payload) => (
                signApi.post(`/signature/envelopes/${uuid}/fields`, payload)
            )));
            const createdUuids = responses.map((response) => response.data?.item?.uuid).filter(Boolean);
            await loadEnvelope({ showLoading: false });
            if (createdUuids.length > 0) {
                selectFields(createdUuids, { focusEditor: true });
            }
            notifySuccess(successMessage || `${createdUuids.length} field${createdUuids.length === 1 ? "" : "s"} added.`);
            return createdUuids;
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to add fields.");
            return [];
        } finally {
            setSaving(false);
        }
    };

    const cloneFieldsToPages = async (sourceFields, pageNumbers, successMessage) => {
        if (!activeDocument?.uuid) return [];
        const pages = [...new Set(pageNumbers)]
            .map((pageNumber) => Math.max(1, Math.round(Number(pageNumber) || 1)))
            .filter((pageNumber) => pageNumber >= 1 && (!activeDocumentPageCount || pageNumber <= activeDocumentPageCount));
        const payloads = [];

        pages.forEach((pageNumber) => {
            sourceFields.forEach((field) => {
                const payload = fieldClonePayload(field, {
                    documentUuid: activeDocument.uuid,
                    pageNumber
                });
                if (fieldsRef.current.some((existing) => fieldsHaveSameLayout(existing, payload))) return;
                payloads.push(payload);
            });
        });

        return createFieldsFromPayloads(payloads, successMessage);
    };

    const copySelectedFields = () => {
        if (selectedFields.length === 0) {
            setError("Select at least one field to copy.");
            return;
        }
        setFieldClipboard(selectedFields.map((field) => fieldClonePayload(field)));
        setError("");
        notifySuccess(`${selectedFields.length} field${selectedFields.length === 1 ? "" : "s"} copied.`);
        focusFieldKeyboardTarget();
    };

    const cutSelectedFields = () => {
        if (!isDraft || saving) return;
        if (selectedFields.length === 0) {
            setError("Select at least one field to cut.");
            return;
        }
        setFieldClipboard(selectedFields.map((field) => fieldClonePayload(field)));
        deleteSelectedFields({ successMessage: "Selected fields cut." });
    };

    const pasteCopiedFields = () => {
        if (!isDraft || saving) return;
        if (!activeDocument?.uuid || fieldClipboard.length === 0) {
            setError("Copy one or more fields before pasting.");
            return;
        }

        const targetPageNumber = clamp(Math.max(1, Math.round(Number(activeAuthoringPageNumber) || 1)), 1, Math.max(1, activeDocumentPageCount || 1));
        const payloads = fieldClipboard.map((field) => {
            const samePage = Number(field.pageNumber || 1) === targetPageNumber && String(field.documentUuid || "") === String(activeDocument.uuid || "");
            const geometry = fieldGeometry(field);
            const offset = samePage ? 0.02 : 0;
            return fieldClonePayload(field, {
                documentUuid: activeDocument.uuid,
                pageNumber: targetPageNumber,
                x: clamp(geometry.x + offset, 0, Math.max(0, 1 - geometry.width)),
                y: clamp(geometry.y + offset, 0, Math.max(0, 1 - geometry.height))
            });
        });

        createFieldsFromPayloads(payloads, "Copied fields pasted.");
    };

    const deleteSelectedFields = async ({ successMessage = "Selected fields deleted." } = {}) => {
        const fieldUuids = [...new Set(selectedFieldUuids)].filter(Boolean);
        if (fieldUuids.length === 0) return;

        setSaving(true);
        setError("");
        try {
            await Promise.all(fieldUuids.map((fieldUuid) => (
                signApi.delete(`/signature/envelopes/${uuid}/fields/${fieldUuid}`)
            )));
            setSelectedFieldUuid("");
            setSelectedFieldUuids([]);
            await loadEnvelope({ showLoading: false });
            notifySuccess(successMessage);
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to delete selected fields.");
        } finally {
            setSaving(false);
        }
    };

    const applySelectedFieldToAllPages = () => {
        const field = selectedField || selectedFields[0];
        if (!field) {
            setError("Select a field before applying it to all pages.");
            return;
        }
        const targetPages = Array.from({ length: activeDocumentPageCount || 0 }, (_, index) => index + 1)
            .filter((pageNumber) => pageNumber !== Number(field.pageNumber || 1));
        cloneFieldsToPages([field], targetPages, "Field cloned to all pages.");
    };

    const selectAllFields = () => {
        const fieldUuids = previewFields.map((field) => field.uuid).filter(Boolean);
        if (fieldUuids.length === 0) return;
        selectFields(fieldUuids, { focusEditor: true });
    };

    editorShortcutsRef.current = {
        alignSelectedFields,
        copySelectedFields,
        cutSelectedFields,
        pasteCopiedFields,
        selectAllFields,
        undoLastFieldChange,
        redoLastFieldChange,
        deleteSelectedFields
    };

    useEffect(() => {
        if (builderStep !== "fields") return undefined;

        const handleKeyDown = (event) => {
            if (shouldIgnoreFieldNudgeTarget(event.target)) return;
            const modified = event.ctrlKey || event.metaKey;
            const key = event.key.toLowerCase();

            if (modified && key === "a") {
                event.preventDefault();
                editorShortcutsRef.current.selectAllFields?.();
                return;
            }

            if (modified && key === "x") {
                event.preventDefault();
                editorShortcutsRef.current.cutSelectedFields?.();
                return;
            }

            if (modified && key === "c") {
                event.preventDefault();
                editorShortcutsRef.current.copySelectedFields?.();
                return;
            }

            if (modified && key === "v") {
                event.preventDefault();
                editorShortcutsRef.current.pasteCopiedFields?.();
                return;
            }

            if (modified && key === "l") {
                event.preventDefault();
                editorShortcutsRef.current.alignSelectedFields?.("left");
                return;
            }

            if (modified && key === "r") {
                event.preventDefault();
                editorShortcutsRef.current.alignSelectedFields?.("right");
                return;
            }

            if (modified && key === "z" && !event.shiftKey) {
                event.preventDefault();
                editorShortcutsRef.current.undoLastFieldChange?.();
                return;
            }

            if ((modified && key === "y") || (modified && event.shiftKey && key === "z")) {
                event.preventDefault();
                editorShortcutsRef.current.redoLastFieldChange?.();
                return;
            }

            if ((event.key === "Delete" || event.key === "Backspace") && selectedFieldUuidsRef.current.length > 0) {
                event.preventDefault();
                editorShortcutsRef.current.deleteSelectedFields?.();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [builderStep]);

    const saveDraftProgress = async () => {
        setSaving(true);
        setError("");

        try {
            await saveEnvelopeOptions();
            await loadEnvelope({ showLoading: false });
            notifySuccess("Draft saved.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to save draft.");
        } finally {
            setSaving(false);
        }
    };

    const convertDraftToTemplate = async () => {
        setSaving(true);
        setError("");

        try {
            await saveEnvelopeOptions();
            const response = await signApi.post(`/signature/envelopes/${uuid}/convert-to-template`, {
                title: String(optionsForm.title || envelope?.title || envelope?.displayName || "Signature Template").trim()
            });
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to convert draft into a template.");
            }
            await loadEnvelope({ showLoading: false });
            notifySuccess("Draft converted into a template.");
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to convert draft into a template.");
        } finally {
            setSaving(false);
        }
    };

    const sendEnvelope = async () => {
        if (sendInFlightRef.current) return;
        if (isTemplateDraft) {
            setError("Create a new envelope from this template before sending for signature.");
            return;
        }

        if (!String(optionsForm.subject || "").trim()) {
            setError("Subject is required before sending.");
            return;
        }

        sendInFlightRef.current = true;
        const recipientsSaved = await saveRecipientRows("cc");
        if (!recipientsSaved) {
            sendInFlightRef.current = false;
            return;
        }

        setSaving(true);
        setError("");

        try {
            await saveEnvelopeOptions();
            const response = await signApi.post(`/signature/envelopes/${uuid}/send`);
            if (!response.data?.success) {
                throw new Error(response.data?.error?.message || response.data?.error || "Unable to send envelope.");
            }
            setSigningLinks(signingLinksFromResponse(response.data));
            setSendOutcome(response.data?.deliveryStatus || "delivered");
            setBuilderStep("final");
            await loadEnvelope({ showLoading: false });
            notifySuccess(response.data?.deliveryStatus === "partial_failure"
                ? "Signature request created. One or more invitations need attention."
                : "Signature request sent.");
        } catch (err) {
            setError(apiErrorMessage(err, "Unable to send envelope."));
        } finally {
            sendInFlightRef.current = false;
            setSaving(false);
        }
    };

    const recordSigningLinkAction = (signerUuid, action) => {
        signApi.post(`/signature/envelopes/${uuid}/recipients/${signerUuid}/signing-link-events`, { action })
            .then(() => loadEnvelopeActivity())
            .catch(() => {});
    };

    const copySignerLink = async (signer) => {
        const signerUuid = signer.uuid || signer.id;
        setSigningLinkAction(`${signerUuid}:copy`);
        setSigningLinksError("");
        try {
            await copySigningLink(signer.signingUrl);
            setCopyFeedback(`Signing link copied for ${signer.name || signer.email || "signer"}.`);
            recordSigningLinkAction(signerUuid, "copied");
            if (copyFeedbackTimerRef.current) window.clearTimeout(copyFeedbackTimerRef.current);
            copyFeedbackTimerRef.current = window.setTimeout(() => setCopyFeedback(""), 3000);
        } catch (err) {
            setSigningLinksError(err.message || "Unable to copy the signing link.");
        } finally {
            setSigningLinkAction("");
        }
    };

    const retrySignerInvitation = async (signer) => {
        const signerUuid = signer.uuid || signer.id;
        setSigningLinkAction(`${signerUuid}:retry`);
        setSigningLinksError("");
        try {
            const response = await signApi.post(`/signature/envelopes/${uuid}/recipients/${signerUuid}/remind`);
            if (response.data?.success === false) {
                throw new Error(response.data?.error?.message || response.data?.error || "Unable to retry the invitation.");
            }
            const nextLinks = signingLinksFromResponse(response.data);
            setSigningLinks(nextLinks);
            setSendOutcome(nextLinks.some((item) => item.deliveryStatus === "delivery_failed") ? "partial_failure" : "delivered");
            if (response.data?.deliveryStatus === "partial_failure") {
                setSigningLinksError(response.data?.delivery?.error?.message || "The signing link is valid, but email delivery still failed.");
            } else {
                notifySuccess(`Invitation sent to ${signer.name || signer.email || "signer"}.`);
            }
            await loadEnvelopeActivity();
        } catch (err) {
            setSigningLinksError(apiErrorMessage(err, "Unable to retry the invitation."));
        } finally {
            setSigningLinkAction("");
        }
    };

    const requestCancelEnvelope = () => {
        if (!isDraft) {
            navigate(-1);
            return;
        }

        setCancelDraftModal({
            show: true,
            confirmation: ""
        });
    };

    const closeCancelDraftModal = () => {
        setCancelDraftModal({
            show: false,
            confirmation: ""
        });
    };

    const confirmCancelEnvelope = async () => {
        if (cancelDraftModal.confirmation !== "DELETE") return;

        closeCancelDraftModal();
        setSaving(true);
        setError("");

        try {
            const response = await signApi.delete(`/signature/envelopes/${uuid}`);
            if (response.data?.success === false) {
                throw new Error(response.data?.error || "Unable to cancel draft.");
            }
            navigate(-1);
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Unable to cancel draft.");
            setSaving(false);
        }
    };

    const goToBuilderStep = async (targetStep) => {
        if (!targetStep || targetStep === builderStep || saving) return;

        const currentIndex = BUILDER_STEPS.findIndex((step) => step.value === builderStep);
        const targetIndex = BUILDER_STEPS.findIndex((step) => step.value === targetStep);

        if (targetIndex < 0) return;

        if (!isDraft) {
            setError("");
            setBuilderStep(targetStep);
            return;
        }

        if (targetIndex < currentIndex) {
            setError("");
            setBuilderStep(targetStep);
            return;
        }

        if (builderStep === "signers") {
            await saveRecipientRows("signer", {
                nextStep: targetStep,
                successMessage: "Signing recipients saved."
            });
            return;
        }

        setError("");
        setBuilderStep(targetStep);
    };

    const renderStepIndicator = () => (
        <div className="d-flex flex-wrap border-bottom mb-3">
            {BUILDER_STEPS.map((step, index) => (
                <button
                    key={step.value}
                    type="button"
                    className={`btn btn-link px-3 py-2 text-decoration-none border-0 border-bottom rounded-0 ${builderStep === step.value ? "text-primary border-primary fw-semibold" : "text-muted"}`}
                    disabled={saving}
                    onClick={() => goToBuilderStep(step.value)}
                    style={{
                        borderBottomWidth: builderStep === step.value ? 2 : 0,
                        marginBottom: -1
                    }}
                >
                    {index + 1}. {step.label}
                </button>
            ))}
        </div>
    );

    const renderSavedRecipients = (items, recipientType) => {
        const showActions = isDraft;
        return (
            <Table size="sm" responsive className="mb-3">
                <thead>
                    <tr>
                        <th>{recipientType === "signer" ? "Signer" : "Recipient"}</th>
                        {showActions ? <th className="text-end">Actions</th> : null}
                    </tr>
                </thead>
                <tbody>
                    {items.map((recipient, index) => {
                        const color = recipientType === "signer" ? signerColor(recipient.uuid, signers) : DEFAULT_FIELD_COLOR;
                        const canReorder = isDraft && !saving && (recipientType !== "signer" || orderedSigning);
                        const required = recipient.isRequired !== false;
                        return (
                        <tr
                            key={recipient.uuid}
                            draggable={canReorder}
                            onDragStart={(event) => handleRecipientDragStart(event, recipient, recipientType)}
                            onDragOver={handleRecipientDragOver}
                            onDrop={(event) => handleRecipientDrop(event, recipient, recipientType)}
                            onDragEnd={handleRecipientDragEnd}
                            style={{ cursor: canReorder ? "grab" : "default" }}
                        >
                            <td>
                                <div className="d-flex align-items-center gap-2">
                                    {recipientType === "signer" && canReorder ? (
                                        <span
                                            className="text-muted d-inline-flex flex-column align-items-center"
                                            title="Drag to reorder"
                                            aria-hidden="true"
                                            style={{ lineHeight: 1 }}
                                        >
                                            <span className="small fw-semibold mb-1">{index + 1}</span>
                                            <CsLineIcons icon="menu" size="15" />
                                        </span>
                                    ) : null}
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 3,
                                            background: color.background,
                                            border: `2px solid ${color.border}`,
                                            display: "inline-block",
                                            flex: "0 0 auto"
                                        }}
                                    />
                                    <div>
                                        <div className="fw-semibold d-flex align-items-center gap-2">
                                            <span>{recipient.name}</span>
                                            {isDigitalSigningContext(recipient) ? <Badge bg="info" text="dark">Digital signing context</Badge> : null}
                                        </div>
                                        <div className="small text-muted">{recipient.email}</div>
                                    </div>
                                </div>
                            </td>
                            {showActions ? (
                            <td className="text-end align-middle">
                                <Dropdown align="end">
                                    <Dropdown.Toggle as={RecipientActionToggle} disabled={!isDraft || saving} />
                                    <Dropdown.Menu renderOnMount popperConfig={RECIPIENT_MENU_POPPER} style={{ zIndex: 1080 }}>
                                        {recipientType === "signer" ? (
                                            <>
                                                <Dropdown.Item onClick={() => previewSignerExperience(recipient)}>
                                                    Preview
                                                </Dropdown.Item>
                                                <Dropdown.Item
                                                    className="d-flex align-items-center justify-content-between gap-3"
                                                    onClick={() => updateSavedRecipient(recipient, { isRequired: !required }, "Signer requirement updated.")}
                                                >
                                                    <span>Required</span>
                                                    <CsLineIcons icon={required ? "check" : "circle"} size="14" />
                                                </Dropdown.Item>
                                            </>
                                        ) : null}
                                        {canReorder ? (
                                            <>
                                                <Dropdown.Item
                                                    disabled={index === 0}
                                                    onClick={() => reorderRecipient(recipient, -1, recipientType)}
                                                >
                                                    Move Up
                                                </Dropdown.Item>
                                                <Dropdown.Item
                                                    disabled={index === items.length - 1}
                                                    onClick={() => reorderRecipient(recipient, 1, recipientType)}
                                                >
                                                    Move Down
                                                </Dropdown.Item>
                                            </>
                                        ) : null}
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                            className="text-danger"
                                            onClick={() => deleteRecipient(recipient.uuid)}
                                        >
                                            Delete
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </td>
                            ) : null}
                        </tr>
                        );
                    })}
                    {items.length === 0 ? (
                        <tr>
                            <td colSpan={showActions ? 2 : 1} className="text-muted">No saved recipients yet.</td>
                        </tr>
                    ) : null}
                </tbody>
            </Table>
        );
    };

    const renderRecipientRows = (recipientType, rows) => {
        if (!isDraft) return null;
        return (
        <>
            {rows.map((row) => (
                <Row className="g-2 align-items-start mb-2" key={row.id}>
                    <Col md="5">
                        <RecipientSuggestInput
                            label="FULL NAME"
                            value={row.name}
                            disabled={!isDraft || saving}
                            onChange={(value) => updateRecipientRow(recipientType, row.id, "name", value)}
                            onSelect={(person) => applyPersonToRecipientRow(recipientType, row.id, person)}
                        />
                    </Col>
                    <Col md="6">
                        <RecipientSuggestInput
                            label="EMAIL ADDRESS"
                            type="email"
                            value={row.email}
                            disabled={!isDraft || saving}
                            onChange={(value) => updateRecipientRow(recipientType, row.id, "email", value)}
                            onSelect={(person) => applyPersonToRecipientRow(recipientType, row.id, person)}
                        />
                        {recipientType === "signer" && String(row.email || "").trim().toLowerCase().endsWith("@sign.invalid") ? (
                            <Badge bg="info" text="dark" className="mt-1">Digital signing context</Badge>
                        ) : null}
                    </Col>
                    <Col md="1" className="d-flex justify-content-end">
                        <Dropdown align="end">
                            <Dropdown.Toggle as={RecipientActionToggle} disabled={!isDraft || saving} />
                            <Dropdown.Menu renderOnMount popperConfig={RECIPIENT_MENU_POPPER} style={{ zIndex: 1080 }}>
                                {recipientType === "signer" ? (
                                    <>
                                        <Dropdown.Item
                                            className="d-flex align-items-center justify-content-between gap-3"
                                            onClick={() => updateRecipientRow(recipientType, row.id, "isRequired", row.isRequired === false)}
                                        >
                                            <span>Required</span>
                                            <CsLineIcons icon={row.isRequired === false ? "circle" : "check"} size="14" />
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                    </>
                                ) : null}
                                <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => removeRecipientRow(recipientType, row.id)}
                                >
                                    Delete
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Col>
                </Row>
            ))}
            <Button
                type="button"
                variant="outline-primary"
                disabled={!isDraft || saving}
                onClick={() => addRecipientRow(recipientType)}
            >
                Add Recipient
            </Button>
        </>
        );
    };

    const renderSignerSetup = () => (
        <Card>
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Card.Title className="mb-0">Signing Recipients</Card.Title>
                    <Badge bg="light" text="dark">{signers.length} saved</Badge>
                </div>
                {renderSavedRecipients(signers, "signer")}
                {renderRecipientRows("signer", signerRows)}
                <div className="border-top pt-3 mt-3">
                    <Form.Check
                        type="checkbox"
                        id="signature-set-signing-order"
                        label="Set order of signing"
                        checked={orderedSigning}
                        disabled={!isDraft || saving}
                        onChange={(event) => updateSigningOrderMode(event.target.checked)}
                    />
                </div>
                <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button variant="outline-secondary" onClick={() => navigate(-1)} disabled={saving}>Back</Button>
                    {isDraft ? (
                        <Button variant="outline-danger" onClick={requestCancelEnvelope} disabled={saving}>Cancel</Button>
                    ) : null}
                    <Button
                        variant="primary"
                        disabled={saving}
                        onClick={() => (
                            isDraft
                                ? saveRecipientRows("signer", { nextStep: "fields", successMessage: "Signing recipients saved." })
                                : setBuilderStep("fields")
                        )}
                    >
                        Next
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );

    const renderFieldSetup = () => (
        <Row className="g-4">
            <Col lg="8">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-start gap-3">
                            <div>
                                <Card.Title>{documentTitle(activeDocument)}</Card.Title>
                                <Card.Text className="text-muted">
                                    Drop fields onto the document pages and drag placed fields to reposition them.
                                </Card.Text>
                            </div>
                            <Badge bg="light" text="dark">{previewFields.length} field{previewFields.length === 1 ? "" : "s"}</Badge>
                        </div>
                        {activeDocument ? (
                            <div
                                ref={fieldKeyboardTargetRef}
                                tabIndex={-1}
                                style={{ outline: "none" }}
                            >
                                <div className="border rounded p-3 mb-3">
                                    <Row className="g-3 align-items-end">
                                        <Col md="5">
                                            <div className="top-label">
                                                <Select
                                                    classNamePrefix="react-select"
                                                    value={selectOption(documentOptions, activeDocument.uuid)}
                                                    onChange={(option) => selectDocument(option?.value || "")}
                                                    isDisabled={saving || documents.length < 2}
                                                    isClearable={false}
                                                    options={documentOptions}
                                                    menuPortalTarget={selectMenuTarget}
                                                    styles={REACT_SELECT_MENU_STYLES}
                                                />
                                                <label>DOCUMENT</label>
                                            </div>
                                        </Col>
                                        <Col md="7">
                                            <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    disabled={saving}
                                                    onClick={() => downloadDocumentPdf(activeDocument, "original")}
                                                >
                                                    <CsLineIcons icon="download" size="15" className="me-1" />
                                                    Original
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    disabled={saving}
                                                    onClick={() => downloadDocumentPdf(activeDocument, "signed")}
                                                >
                                                    <CsLineIcons icon="download" size="15" className="me-1" />
                                                    Signed
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    disabled={saving}
                                                    onClick={() => downloadDocumentPdf(activeDocument, "audit")}
                                                >
                                                    <CsLineIcons icon="file-text" size="15" className="me-1" />
                                                    Audit
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    disabled={saving}
                                                    onClick={() => downloadEnvelopePacket("signed")}
                                                >
                                                    <CsLineIcons icon="archive" size="15" className="me-1" />
                                                    Packet
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                        <Badge bg="light" text="dark">{documentPageCount(activeDocument) || "?"} page{documentPageCount(activeDocument) === 1 ? "" : "s"}</Badge>
                                        <Badge bg="light" text="dark">{documentDimensionsSummary(activeDocument)}</Badge>
                                        <Badge bg="light" text="dark">{documentOrientationSummary(activeDocument)}</Badge>
                                        <Badge bg="light" text="dark">{documentRotationSummary(activeDocument)}</Badge>
                                        {documentConversion(activeDocument).required ? (
                                            <Badge bg={documentConversion(activeDocument).succeeded ? "success" : "warning"} text={documentConversion(activeDocument).succeeded ? undefined : "dark"}>
                                                {documentConversion(activeDocument).succeeded ? "converted" : "conversion required"}
                                            </Badge>
                                        ) : null}
                                        {documentMetadata(activeDocument).formsFlattened ? <Badge bg="success">forms flattened</Badge> : null}
                                    </div>
                                    {documentIsDocxSource(activeDocument) ? (
                                        <div className="small text-muted mt-2">
                                            {documentConversionSummary(activeDocument)}
                                        </div>
                                    ) : null}
                                    {isDraft ? (
                                        <div className="d-flex flex-wrap gap-2 mt-3">
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                disabled={saving}
                                                onClick={requestAddDocument}
                                            >
                                                <CsLineIcons icon="plus" size="15" className="me-1" />
                                                Add
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={saving || documents.length < 2 || documents.findIndex((document) => document.uuid === activeDocument.uuid) <= 0}
                                                onClick={() => reorderActiveDocument(-1)}
                                            >
                                                <CsLineIcons icon="arrow-top" size="15" className="me-1" />
                                                Up
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={saving || documents.length < 2 || documents.findIndex((document) => document.uuid === activeDocument.uuid) >= documents.length - 1}
                                                onClick={() => reorderActiveDocument(1)}
                                            >
                                                <CsLineIcons icon="arrow-bottom" size="15" className="me-1" />
                                                Down
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                disabled={saving}
                                                onClick={requestReplaceDocument}
                                            >
                                                <CsLineIcons icon="upload" size="15" className="me-1" />
                                                Replace
                                            </Button>
                                            {documentIsDocxSource(activeDocument) ? (
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    disabled={saving}
                                                    onClick={requestReconvertActiveDocument}
                                                >
                                                    <CsLineIcons icon="rotate-left" size="15" className="me-1" />
                                                    Re-convert
                                                </Button>
                                            ) : null}
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={saving || documentPageCount(activeDocument) < 2}
                                                onClick={requestSplitActiveDocument}
                                            >
                                                <CsLineIcons icon="scissors" size="15" className="me-1" />
                                                Split Pages
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                disabled={saving || !activeDocument?.uuid}
                                                onClick={requestRemoveActiveDocument}
                                            >
                                                Remove
                                            </Button>
                                            <input
                                                ref={addFileInputRef}
                                                type="file"
                                                className="d-none"
                                                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                onChange={addDocumentFile}
                                            />
                                            <input
                                                ref={replaceFileInputRef}
                                                type="file"
                                                className="d-none"
                                                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                onChange={replaceActiveDocumentFile}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                                <SignatureDocumentCanvas
                                    envelopeUuid={uuid}
                                    document={activeDocument}
                                    fields={previewFields}
                                    signers={signers}
                                    selectedFieldUuids={selectedFieldUuids}
                                    activePageNumber={activeAuthoringPageNumber}
                                    onActivePageChange={setActiveAuthoringPageNumber}
                                    editable={isDraft && !saving}
                                    snapToGrid={snapToGrid}
                                    onSelectField={selectField}
                                    onSelectFields={selectFields}
                                    onDropNewField={dropNewField}
                                    onMoveField={moveField}
                                    onResizeField={moveField}
                                    onDeleteField={deleteField}
                                />
                            </div>
                        ) : <Alert variant="warning">No document attached.</Alert>}
                    </Card.Body>
                </Card>
            </Col>

            <Col lg="4">
                <div style={FIELD_SIDEBAR_STYLE}>
                <Card className="mb-3">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Card.Title className="mb-0">Field Tools</Card.Title>
                            <Form.Check
                                className="mb-0"
                                type="switch"
                                id="signature-field-snap-grid"
                                label="Snap"
                                checked={snapToGrid}
                                disabled={!isDraft || saving}
                                onChange={(event) => setSnapToGrid(event.target.checked)}
                            />
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                className="btn-icon btn-icon-only"
                                title="Undo"
                                disabled={!isDraft || saving || fieldHistory.undo.length === 0}
                                onClick={undoLastFieldChange}
                            >
                                <CsLineIcons icon="rotate-left" size="15" />
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                className="btn-icon btn-icon-only"
                                title="Redo"
                                disabled={!isDraft || saving || fieldHistory.redo.length === 0}
                                onClick={redoLastFieldChange}
                            >
                                <CsLineIcons icon="rotate-right" size="15" />
                            </Button>
                            <span className="vr d-none d-sm-inline-block" />
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                title="Copy selected fields"
                                disabled={!isDraft || saving || selectedFieldUuids.length === 0}
                                onClick={copySelectedFields}
                            >
                                <CsLineIcons icon="duplicate" size="15" className="me-1" />
                                Copy
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                title="Paste copied fields onto the active page"
                                disabled={!isDraft || saving || fieldClipboard.length === 0}
                                onClick={pasteCopiedFields}
                            >
                                <CsLineIcons icon="clipboard" size="15" className="me-1" />
                                Paste
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-danger"
                                title="Delete selected fields"
                                disabled={!isDraft || saving || selectedFieldUuids.length === 0}
                                onClick={deleteSelectedFields}
                            >
                                <CsLineIcons icon="bin" size="15" className="me-1" />
                                Delete
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                title="Apply the selected field to every page"
                                disabled={!isDraft || saving || !selectedField || activeDocumentPageCount < 2}
                                onClick={applySelectedFieldToAllPages}
                            >
                                <CsLineIcons icon="file-text" size="15" className="me-1" />
                                Clone All
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Card.Title className="mb-0">Fields</Card.Title>
                            <Badge bg="light" text="dark">{previewFields.length}</Badge>
                        </div>

                        <div className="top-label mb-3">
                            <Select
                                classNamePrefix="react-select"
                                value={selectOption(signerOptions, fieldForm.recipientUuid)}
                                onChange={(option) => updateFieldForm("recipientUuid", option?.value || "")}
                                isDisabled={!isDraft || saving}
                                isClearable
                                options={signerOptions}
                                placeholder="Choose signer..."
                                menuPortalTarget={selectMenuTarget}
                                styles={REACT_SELECT_MENU_STYLES}
                            />
                            <label>SIGNER</label>
                        </div>

                        <Form.Label>STANDARD FIELDS</Form.Label>
                        <div className="d-flex gap-2 flex-wrap mb-3">
                            {STANDARD_FIELDS.map((field) => (
                                <Button
                                    key={field.value}
                                    size="sm"
                                    variant="outline-secondary"
                                    draggable={isDraft && !saving && !!activeDocument?.uuid && !!fieldForm.recipientUuid}
                                    disabled={!isDraft || saving || !activeDocument?.uuid || !fieldForm.recipientUuid}
                                    onDragStart={(event) => handlePaletteDragStart(event, field.value)}
                                    onClick={() => addStandardField(field.value)}
                                >
                                    {field.label}
                                </Button>
                            ))}
                        </div>

                        <Table size="sm" responsive className="mb-0">
                            <tbody>
                                {previewFields.map((field) => {
                                    const recipient = recipients.find((item) => item.uuid === field.recipientUuid);
                                    const color = signerColor(field.recipientUuid, signers);
                                    const active = selectedFieldUuids.includes(field.uuid);
                                    return (
                                        <tr
                                            key={field.uuid}
                                            tabIndex={0}
                                            onClick={(event) => selectField(field.uuid, { additive: event.ctrlKey || event.metaKey, focusEditor: true })}
                                            onKeyDown={(event) => {
                                                if (event.key !== "Enter" && event.key !== " ") return;
                                                event.preventDefault();
                                                selectField(field.uuid, { additive: event.ctrlKey || event.metaKey, focusEditor: true });
                                            }}
                                            style={{ cursor: "pointer", background: active ? "rgba(13, 110, 253, 0.08)" : undefined }}
                                        >
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span
                                                        aria-hidden="true"
                                                        style={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: 3,
                                                            background: color.background,
                                                            border: `2px solid ${color.border}`,
                                                            display: "inline-block",
                                                            flex: "0 0 auto"
                                                        }}
                                                    />
                                                    <div>
                                                        <div className="fw-semibold">{field.label || fieldTypeLabel(field.fieldType)}</div>
                                                        <div className="small text-muted">
                                                            Page {field.pageNumber} - {recipientName(recipient)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-end"><Badge bg="light" text="dark">{fieldTypeLabel(field.fieldType)}</Badge></td>
                                        </tr>
                                    );
                                })}
                                {previewFields.length === 0 ? <tr><td className="text-muted">No fields yet.</td></tr> : null}
                            </tbody>
                        </Table>

                        {selectedFieldUuids.length > 1 ? (
                            <div className="border rounded p-3 mt-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="mb-0">ALIGN SELECTED FIELDS</Form.Label>
                                    <Badge bg="light" text="dark">{selectedFieldUuids.length} selected</Badge>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    <AlignmentIconButtons
                                        value=""
                                        activeSelection={false}
                                        disabled={!isDraft || saving}
                                        onChange={alignSelectedFields}
                                    />
                                    <AlignmentIconButtons
                                        value=""
                                        activeSelection={false}
                                        disabled={!isDraft || saving}
                                        alignments={MULTI_FIELD_VERTICAL_ALIGNMENTS}
                                        onChange={alignSelectedFields}
                                    />
                                </div>
                            </div>
                        ) : null}

                        {selectedField ? (
                            <div className="border rounded p-3 mt-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="fw-semibold">{selectedFieldUuids.length > 1 ? "Selected Fields" : "Selected Field"}</div>
                                    <Badge bg="light" text="dark">
                                        {selectedFieldUuids.length > 1 ? `${selectedFieldUuids.length} selected` : fieldTypeLabel(selectedField.fieldType)}
                                    </Badge>
                                </div>
                                <div className="top-label mb-3">
                                    <Select
                                        classNamePrefix="react-select"
                                        value={selectOption(signerOptions, selectedFieldForm.recipientUuid)}
                                        onChange={(option) => updateSelectedFieldForm("recipientUuid", option?.value || "")}
                                        isDisabled={!isDraft || saving || selectedFieldUuids.length > 1}
                                        isClearable
                                        options={signerOptions}
                                        placeholder="Choose signer..."
                                        menuPortalTarget={selectMenuTarget}
                                        styles={REACT_SELECT_MENU_STYLES}
                                    />
                                    <label>SIGNER</label>
                                </div>
                                <Row className="g-2 align-items-start">
                                    <Col sm="7">
                                        <div className="top-label mb-3">
                                            <Form.Control value={selectedFieldForm.label} onChange={(event) => updateSelectedFieldForm("label", event.target.value)} disabled={!isDraft || saving || selectedFieldUuids.length > 1} />
                                            <label className="d-flex align-items-start gap-1">
                                                <span>NAME</span>
                                                {selectedFieldNameWarning ? (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        delay={{ show: 150, hide: 0 }}
                                                        overlay={<Tooltip id={`signature-field-name-warning-${selectedField.uuid}`}>{selectedFieldNameWarning}</Tooltip>}
                                                    >
                                                        <span
                                                            className="text-warning d-inline-flex align-items-center"
                                                            role="button"
                                                            tabIndex={0}
                                                            aria-label={selectedFieldNameWarning}
                                                            onMouseDown={(event) => event.preventDefault()}
                                                        >
                                                            <span
                                                                aria-hidden="true"
                                                                style={{
                                                                    width: 0,
                                                                    height: 0,
                                                                    borderLeft: "3px solid transparent",
                                                                    borderRight: "3px solid transparent",
                                                                    borderBottom: "6px solid currentColor",
                                                                    marginTop: -1
                                                                }}
                                                            />
                                                        </span>
                                                    </OverlayTrigger>
                                                ) : null}
                                            </label>
                                        </div>
                                    </Col>
                                    <Col sm="5">
                                        <div className="top-label mb-3">
                                            <Form.Control
                                                value={selectedFieldForm.apiName}
                                                onChange={(event) => updateSelectedFieldForm("apiName", event.target.value)}
                                                disabled={!isDraft || saving || selectedFieldUuids.length > 1}
                                                isInvalid={Boolean(selectedFieldApiNameWarning)}
                                            />
                                            <label>API FIELD NAME</label>
                                            {selectedFieldApiNameWarning ? <Form.Text className="text-danger">{selectedFieldApiNameWarning}</Form.Text> : <Form.Text>Exact lowercase key used by template/API callers.</Form.Text>}
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="g-2 align-items-start">
                                    <Col sm="4" className="pt-2">
                                        <Form.Check
                                            className="mb-3"
                                            type="switch"
                                            label="Required"
                                            checked={selectedFieldForm.required}
                                            disabled={!isDraft || saving}
                                            onChange={(event) => updateSelectedFieldForm("required", event.target.checked)}
                                        />
                                    </Col>
                                </Row>
                                <Row className="g-2 align-items-end">
                                    <Col sm="6">
                                        <Form.Label className="small text-muted mb-1">POSITION</Form.Label>
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                max="1"
                                                step="0.001"
                                                value={selectedFieldForm.x}
                                                aria-label="Position X"
                                                onChange={(event) => updateSelectedFieldForm("x", event.target.value)}
                                                onBlur={(event) => updateSelectedFieldForm("x", fieldDecimal(event.target.value, selectedFieldForm.x))}
                                                disabled={!isDraft || saving}
                                            />
                                            <span className="text-muted">x</span>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                max="1"
                                                step="0.001"
                                                value={selectedFieldForm.y}
                                                aria-label="Position Y"
                                                onChange={(event) => updateSelectedFieldForm("y", event.target.value)}
                                                onBlur={(event) => updateSelectedFieldForm("y", fieldDecimal(event.target.value, selectedFieldForm.y))}
                                                disabled={!isDraft || saving}
                                            />
                                        </div>
                                    </Col>
                                    <Col sm="6">
                                        <Form.Label className="small text-muted mb-1">SIZE</Form.Label>
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <Form.Control
                                                type="number"
                                                min="0.02"
                                                max="1"
                                                step="0.001"
                                                value={selectedFieldForm.width}
                                                aria-label="Width"
                                                onChange={(event) => updateSelectedFieldForm("width", event.target.value)}
                                                onBlur={(event) => updateSelectedFieldForm("width", fieldDecimal(event.target.value, selectedFieldForm.width))}
                                                disabled={!isDraft || saving}
                                            />
                                            <span className="text-muted">x</span>
                                            <Form.Control
                                                type="number"
                                                min="0.02"
                                                max="1"
                                                step="0.001"
                                                value={selectedFieldForm.height}
                                                aria-label="Height"
                                                onChange={(event) => updateSelectedFieldForm("height", event.target.value)}
                                                onBlur={(event) => updateSelectedFieldForm("height", fieldDecimal(event.target.value, selectedFieldForm.height))}
                                                disabled={!isDraft || saving}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="g-2">
                                    <Col sm="5">
                                        <div className="top-label mb-3">
                                            <Select
                                                classNamePrefix="react-select"
                                                value={selectOption(FIELD_FONTS, selectedFieldForm.fontFamily)}
                                                onChange={(option) => updateSelectedFieldForm("fontFamily", option?.value || DEFAULT_FIELD_METADATA.fontFamily)}
                                                isDisabled={!isDraft || saving}
                                                isClearable={false}
                                                options={FIELD_FONTS}
                                                menuPortalTarget={selectMenuTarget}
                                                styles={REACT_SELECT_MENU_STYLES}
                                            />
                                            <label>FONT</label>
                                        </div>
                                    </Col>
                                    <Col sm="3">
                                        <div className="top-label mb-3">
                                            <Select
                                                classNamePrefix="react-select"
                                                value={selectOption(FIELD_FONT_SIZE_OPTIONS, selectedFieldForm.fontSize)}
                                                onChange={(option) => updateSelectedFieldForm("fontSize", option?.value || DEFAULT_FIELD_METADATA.fontSize)}
                                                isDisabled={!isDraft || saving}
                                                isClearable={false}
                                                options={FIELD_FONT_SIZE_OPTIONS}
                                                menuPortalTarget={selectMenuTarget}
                                                styles={REACT_SELECT_MENU_STYLES}
                                            />
                                            <label>SIZE</label>
                                        </div>
                                    </Col>
                                    <Col sm="4">
                                        <div className="mb-3">
                                            <AlignmentIconButtons
                                                value={selectedFieldForm.textAlign}
                                                disabled={!isDraft || saving}
                                                onChange={(value) => updateSelectedFieldForm("textAlign", value)}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <div className="top-label mb-3">
                                    <Form.Control
                                        as="textarea"
                                        rows="3"
                                        value={selectedFieldForm.validation}
                                        onChange={(event) => updateSelectedFieldForm("validation", event.target.value)}
                                        disabled={!isDraft || saving || selectedFieldUuids.length > 1}
                                    />
                                    <label>VALIDATION</label>
                                </div>
                            </div>
                        ) : null}

                        <div className="d-flex justify-content-between gap-2 mt-4">
                            <Button variant="outline-secondary" onClick={() => setBuilderStep("signers")} disabled={saving}>Back</Button>
                            <div className="d-flex gap-2">
                                {isDraft ? (
                                    <Button variant="outline-danger" onClick={requestCancelEnvelope} disabled={saving}>Cancel</Button>
                                ) : null}
                                <Button variant="primary" onClick={() => setBuilderStep("final")} disabled={saving}>Next</Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                </div>
            </Col>
        </Row>
    );

    const renderSigningLinks = () => {
        if (isDraft && signingLinks.length === 0) return null;
        const deliveryFailures = signingLinks.filter((signer) => signer.deliveryStatus === "delivery_failed");

        return (
            <Col xs="12">
                <Card>
                    <Card.Body>
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                            <div>
                                <Card.Title className="mb-1">Signing Links</Card.Title>
                                <Card.Text className="text-muted mb-0">
                                    Each link is unique to its signer. Share it only with the person named below.
                                </Card.Text>
                            </div>
                            <Button type="button" variant="outline-secondary" size="sm" disabled={loadingSigningLinks} onClick={() => loadSigningLinks()}>
                                {loadingSigningLinks ? <Spinner animation="border" size="sm" className="me-1" /> : <CsLineIcons icon="rotate-left" size="15" className="me-1" />}
                                Refresh
                            </Button>
                        </div>
                        <div className={copyFeedback ? "text-success small mb-3" : "visually-hidden"} aria-live="polite">{copyFeedback}</div>
                        {sendOutcome === "delivered" && deliveryFailures.length === 0 ? (
                            <Alert variant="success">The signature request was created and all current invitations were delivered.</Alert>
                        ) : null}
                        {sendOutcome === "partial_failure" || deliveryFailures.length > 0 ? (
                            <Alert variant="warning">
                                The signature request and links are active, but {deliveryFailures.length || "one or more"} invitation{deliveryFailures.length === 1 ? "" : "s"} could not be delivered. Copy the link now or retry the invitation.
                            </Alert>
                        ) : null}
                        {signingLinksError ? <Alert variant="danger">{signingLinksError}</Alert> : null}
                        {loadingSigningLinks && signingLinks.length === 0 ? (
                            <div className="py-4 text-center text-muted">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading signing links...
                            </div>
                        ) : null}
                        <div className="d-grid gap-3">
                            {signingLinks.map((signer) => {
                                const signerUuid = signer.uuid || signer.id;
                                const failed = signer.deliveryStatus === "delivery_failed";
                                const delivered = signer.deliveryStatus === "delivered";
                                const actionBusy = signingLinkAction.startsWith(`${signerUuid}:`);
                                return (
                                    <div className="border rounded p-3" key={signerUuid}>
                                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                                            <div>
                                                <div className="fw-semibold">{signer.name || "Signer"}</div>
                                                <div className="text-muted small">{signer.email}</div>
                                            </div>
                                            <Badge bg={failed ? "warning" : delivered ? "success" : "secondary"} text={failed ? "dark" : undefined}>
                                                {failed ? "Email delivery failed" : delivered ? "Invitation delivered" : signer.eligible ? "Ready to share" : "Waiting for prior signer"}
                                            </Badge>
                                        </div>
                                        {signer.signingUrl ? (
                                            <>
                                                <Form.Label className="visually-hidden" htmlFor={`signing-link-${signerUuid}`}>Signing link for {signer.name || signer.email}</Form.Label>
                                                <div className="d-flex flex-column flex-lg-row gap-2">
                                                    <Form.Control id={`signing-link-${signerUuid}`} value={signer.signingUrl} readOnly onFocus={(event) => event.target.select()} />
                                                    <div className="d-flex flex-wrap gap-2 flex-shrink-0">
                                                        <Button type="button" variant="outline-primary" disabled={actionBusy} onClick={() => copySignerLink(signer)}>
                                                            {signingLinkAction === `${signerUuid}:copy` ? <Spinner animation="border" size="sm" className="me-1" /> : <CsLineIcons icon="clipboard" size="15" className="me-1" />}
                                                            Copy Link
                                                        </Button>
                                                        <Button
                                                            as="a"
                                                            href={signer.signingUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            variant="outline-secondary"
                                                            onClick={() => recordSigningLinkAction(signerUuid, "opened")}
                                                        >
                                                            <CsLineIcons icon="link" size="15" className="me-1" />
                                                            Open Signing Page
                                                        </Button>
                                                        {failed && signer.eligible ? (
                                                            <Button type="button" variant="primary" disabled={actionBusy} onClick={() => retrySignerInvitation(signer)}>
                                                                {signingLinkAction === `${signerUuid}:retry` ? <Spinner animation="border" size="sm" className="me-1" /> : <CsLineIcons icon="send" size="15" className="me-1" />}
                                                                Retry Invitation
                                                            </Button>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-muted small">A signing link is no longer available for this recipient.</div>
                                        )}
                                        {failed && signer.deliveryError?.message ? <div className="text-danger small mt-2">{signer.deliveryError.message}</div> : null}
                                        {!signer.eligible && signer.available ? <div className="text-muted small mt-2">This link becomes usable after the earlier routing step is complete.</div> : null}
                                    </div>
                                );
                            })}
                        </div>
                        {!loadingSigningLinks && signingLinks.length === 0 ? (
                            <Alert variant="secondary" className="mb-0">No active signer links are available for this request.</Alert>
                        ) : null}
                    </Card.Body>
                </Card>
            </Col>
        );
    };

    const renderFinalSetup = () => (
        <Row className="g-4">
            {renderSigningLinks()}
            <Col lg="6">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Card.Title className="mb-0">Non-Signing Recipients</Card.Title>
                            <Badge bg="light" text="dark">{ccRecipients.length} saved</Badge>
                        </div>
                        {renderSavedRecipients(ccRecipients, "cc")}
                        {renderRecipientRows("cc", ccRows)}
                    </Card.Body>
                </Card>
            </Col>
            <Col lg="6">
                    <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Card.Title className="mb-0">Delivery</Card.Title>
                            {isTemplateDraft ? <Badge bg="info">Template</Badge> : null}
                        </div>
                        {isTemplateDraft ? (
                            <Alert variant="info">
                                This draft is a template. Create a new envelope from the template before sending.
                            </Alert>
                        ) : null}
                        <div className="top-label mb-3">
                            <Form.Control value={optionsForm.title} onChange={(event) => updateOptionsForm("title", event.target.value)} disabled={!isDraft || saving} />
                            <label>ENVELOPE TITLE</label>
                        </div>
                        <div className="top-label mb-3">
                            <Form.Control value={optionsForm.subject} onChange={(event) => updateOptionsForm("subject", event.target.value)} disabled={!isDraft || saving} />
                            <label>SUBJECT</label>
                        </div>
                        <div className="top-label mb-3">
                            <Form.Control as="textarea" rows="5" value={optionsForm.messageBody} onChange={(event) => updateOptionsForm("messageBody", event.target.value)} disabled={!isDraft || saving} />
                            <label>MESSAGE</label>
                        </div>
                        {SIGNATURE_METADATA_FIELDS_ENABLED ? (
                            <Row className="g-2">
                                <Col sm="6">
                                    <div className="top-label mb-3">
                                        <Form.Control as="textarea" rows="3" value={optionsForm.internalNotes} onChange={(event) => updateOptionsForm("internalNotes", event.target.value)} disabled={!isDraft || saving} />
                                        <label>INTERNAL NOTES</label>
                                    </div>
                                </Col>
                                <Col sm="6">
                                    <div className="top-label mb-3">
                                        <Form.Control as="textarea" rows="3" value={optionsForm.externalNotes} onChange={(event) => updateOptionsForm("externalNotes", event.target.value)} disabled={!isDraft || saving} />
                                        <label>EXTERNAL NOTES</label>
                                    </div>
                                </Col>
                            </Row>
                        ) : null}
                        <Row className="g-2">
                            <Col sm="6">
                                <div className="top-label mb-3">
                                    <Form.Control type="date" value={optionsForm.expirationAt} onChange={(event) => updateOptionsForm("expirationAt", event.target.value)} disabled={!isDraft || saving} />
                                    <label>EXPIRES</label>
                                </div>
                            </Col>
                            <Col sm="6">
                                <div className="top-label mb-3">
                                    <Form.Control type="date" value={optionsForm.defaultReminderEndsAt} onChange={(event) => updateOptionsForm("defaultReminderEndsAt", event.target.value)} disabled={!isDraft || saving} />
                                    <label>REMINDERS UNTIL</label>
                                </div>
                            </Col>
                        </Row>
                        <div className="top-label mb-3">
                            <Select
                                classNamePrefix="react-select"
                                value={selectOption(REMINDER_FREQUENCY_OPTIONS, optionsForm.defaultReminderFrequency)}
                                onChange={(option) => updateOptionsForm("defaultReminderFrequency", option?.value || "daily")}
                                isDisabled={!isDraft || saving}
                                isClearable={false}
                                options={REMINDER_FREQUENCY_OPTIONS}
                                menuPortalTarget={selectMenuTarget}
                                styles={REACT_SELECT_MENU_STYLES}
                            />
                            <label>REMINDER FREQUENCY</label>
                        </div>
                        {SIGNATURE_METADATA_FIELDS_ENABLED ? (
                            <Row className="g-2">
                                <Col sm="4">
                                    <div className="top-label mb-3">
                                        <Form.Control value={optionsForm.category} onChange={(event) => updateOptionsForm("category", event.target.value)} disabled={!isDraft || saving} />
                                        <label>CATEGORY</label>
                                    </div>
                                </Col>
                                <Col sm="4">
                                    <div className="top-label mb-3">
                                        <Form.Control value={optionsForm.practiceArea} onChange={(event) => updateOptionsForm("practiceArea", event.target.value)} disabled={!isDraft || saving} />
                                        <label>PRACTICE AREA</label>
                                    </div>
                                </Col>
                                <Col sm="4">
                                    <div className="top-label mb-3">
                                        <Form.Control value={optionsForm.documentType} onChange={(event) => updateOptionsForm("documentType", event.target.value)} disabled={!isDraft || saving} />
                                        <label>DOCUMENT TYPE</label>
                                    </div>
                                </Col>
                            </Row>
                        ) : null}
                        <Row className="g-2">
                            <Col sm={SIGNATURE_METADATA_FIELDS_ENABLED ? 6 : 12}>
                                <TagTermsEditor
                                    value={optionsForm.tags}
                                    disabled={!isDraft || saving}
                                    onChange={(value) => updateOptionsForm("tags", value)}
                                />
                            </Col>
                        </Row>
                        <div className="d-flex justify-content-between gap-2 mt-4">
                            <Button variant="outline-secondary" onClick={() => setBuilderStep("fields")} disabled={saving}>Back</Button>
                            {isDraft ? (
                                <div className="d-flex gap-2 flex-wrap justify-content-end">
                                    <Button variant="outline-primary" disabled={saving} onClick={saveDraftProgress}>
                                        Save Progress
                                    </Button>
                                    {SIGNATURE_TEMPLATE_CONVERSION_ENABLED ? (
                                        <Button variant="outline-secondary" disabled={saving || isTemplateDraft} onClick={convertDraftToTemplate}>
                                            Convert to Template
                                        </Button>
                                    ) : null}
                                    <Button variant="outline-danger" onClick={requestCancelEnvelope} disabled={saving}>Cancel</Button>
                                    <Button variant="primary" disabled={saving || isTemplateDraft} onClick={sendEnvelope}>
                                        Send for Signature
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );

    const renderActivityReview = () => (
        <Card>
            <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <div>
                        <Card.Title className="mb-0">Activity</Card.Title>
                        <Card.Text className="text-muted mb-0">Recorded signature request events.</Card.Text>
                    </div>
                    <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        disabled={loadingActivity}
                        onClick={loadEnvelopeActivity}
                    >
                        <CsLineIcons icon="rotate-left" size="15" className="me-1" />
                        Refresh
                    </Button>
                </div>
                {activityError ? <Alert variant="warning">{activityError}</Alert> : null}
                {loadingActivity ? (
                    <div className="py-4 text-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span className="text-muted">Loading activity...</span>
                    </div>
                ) : (
                    <Table responsive hover size="sm" className="mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: "20%" }}>When</th>
                                <th style={{ width: "20%" }}>Event</th>
                                <th style={{ width: "25%" }}>Related To</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {signatureEvents.map((event) => {
                                const recipient = recipientsByUuid.get(event.recipientUuid);
                                const document = documentsByUuid.get(event.documentUuid);
                                const related = [
                                    recipient ? recipientName(recipient) : "",
                                    document ? documentTitle(document) : ""
                                ].filter(Boolean).join(" / ") || "Envelope";
                                return (
                                    <tr key={event.uuid || `${event.eventType}-${event.eventAt}`}>
                                        <td className="text-nowrap">{formatDateTime(event.eventAt || event.createdAt) || "-"}</td>
                                        <td>{signatureEventLabel(event.eventType)}</td>
                                        <td>{related}</td>
                                        <td className="text-muted">{signatureEventDetails(event) || "-"}</td>
                                    </tr>
                                );
                            })}
                            {signatureEvents.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="text-muted">No activity has been recorded yet.</td>
                                </tr>
                            ) : null}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    if (loading) {
        return (
            <Col xs="12" className="py-5 text-center">
                <Spinner animation="border" />
            </Col>
        );
    }

    return (
        <Col xs="12">
            {renderStepIndicator()}
            {error ? <Alert variant="danger">{error}</Alert> : null}

            {builderStep === "signers" ? renderSignerSetup() : null}
            {builderStep === "fields" ? renderFieldSetup() : null}
            {builderStep === "final" ? renderFinalSetup() : null}
            {builderStep === "activity" ? renderActivityReview() : null}
            <ConfirmModal
                show={cancelDraftModal.show}
                title="Cancel Signature Draft"
                body="This will delete the draft signature request and its temporary signing document."
                onCancel={closeCancelDraftModal}
                buttons={[
                    {
                        caption: "delete",
                        variant: "danger",
                        display: true,
                        enabled: cancelDraftModal.confirmation === "DELETE" && !saving,
                        onEnabledClick: confirmCancelEnvelope
                    },
                    {
                        default: true,
                        caption: "cancel",
                        variant: "primary",
                        display: true,
                        enabled: true,
                        onEnabledClick: closeCancelDraftModal
                    }
                ]}
            >
                <div className="mt-3">
                    <div className="text-muted mb-3">Type DELETE to confirm.</div>
                    <div className="top-label">
                        <Form.Control
                            value={cancelDraftModal.confirmation}
                            onChange={(event) => setCancelDraftModal((prev) => ({ ...prev, confirmation: event.target.value }))}
                            autoFocus
                        />
                        <Form.Label>CONFIRMATION TEXT</Form.Label>
                    </div>
                </div>
            </ConfirmModal>
            <ConfirmModal
                show={removeDocumentModal.show}
                title="Remove Document"
                body={`This will remove ${documentTitle(activeDocument)} from the draft and delete any fields placed on it.`}
                onCancel={closeRemoveDocumentModal}
                buttons={[
                    {
                        caption: "remove",
                        variant: "danger",
                        display: true,
                        enabled: removeDocumentModal.confirmation === "REMOVE" && !saving,
                        onEnabledClick: confirmRemoveActiveDocument
                    },
                    {
                        default: true,
                        caption: "cancel",
                        variant: "primary",
                        display: true,
                        enabled: true,
                        onEnabledClick: closeRemoveDocumentModal
                    }
                ]}
            >
                <div className="mt-3">
                    <div className="text-muted mb-3">Type REMOVE to confirm.</div>
                    <div className="top-label">
                        <Form.Control
                            value={removeDocumentModal.confirmation}
                            onChange={(event) => setRemoveDocumentModal((prev) => ({ ...prev, confirmation: event.target.value }))}
                            autoFocus
                        />
                        <Form.Label>CONFIRMATION TEXT</Form.Label>
                    </div>
                </div>
            </ConfirmModal>
            <ConfirmModal
                show={splitDocumentModal.show}
                title="Split Document"
                body="This will split the selected document into one document per page and move any placed fields to the matching new page document."
                onCancel={closeSplitDocumentModal}
                buttons={[
                    {
                        caption: "split",
                        variant: "primary",
                        display: true,
                        enabled: splitDocumentModal.confirmation === "SPLIT" && !saving,
                        onEnabledClick: confirmSplitActiveDocument
                    },
                    {
                        default: true,
                        caption: "cancel",
                        variant: "outline-secondary",
                        display: true,
                        enabled: true,
                        onEnabledClick: closeSplitDocumentModal
                    }
                ]}
            >
                <div className="mt-3">
                    <div className="text-muted mb-3">Type SPLIT to confirm.</div>
                    <div className="top-label">
                        <Form.Control
                            value={splitDocumentModal.confirmation}
                            onChange={(event) => setSplitDocumentModal((prev) => ({ ...prev, confirmation: event.target.value }))}
                            autoFocus
                        />
                        <Form.Label>CONFIRMATION TEXT</Form.Label>
                    </div>
                </div>
            </ConfirmModal>
            <ConfirmModal
                show={reconvertDocumentModal.show}
                title="Re-convert DOCX"
                body="This will regenerate the PDF signing copy from the stored DOCX source. If page geometry changes, placed fields will be cleared."
                onCancel={closeReconvertDocumentModal}
                buttons={[
                    {
                        caption: "re-convert",
                        variant: "primary",
                        display: true,
                        enabled: reconvertDocumentModal.confirmation === "RECONVERT" && !saving,
                        onEnabledClick: confirmReconvertActiveDocument
                    },
                    {
                        default: true,
                        caption: "cancel",
                        variant: "outline-secondary",
                        display: true,
                        enabled: true,
                        onEnabledClick: closeReconvertDocumentModal
                    }
                ]}
            >
                <div className="mt-3">
                    <div className="text-muted mb-3">Type RECONVERT to confirm.</div>
                    <div className="top-label">
                        <Form.Control
                            value={reconvertDocumentModal.confirmation}
                            onChange={(event) => setReconvertDocumentModal((prev) => ({ ...prev, confirmation: event.target.value }))}
                            autoFocus
                        />
                        <Form.Label>CONFIRMATION TEXT</Form.Label>
                    </div>
                </div>
            </ConfirmModal>
            <Modal show={signerPreviewModal.show} onHide={closeSignerPreviewModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Signer Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {signerPreviewModal.loading ? (
                        <div className="py-4 text-center"><Spinner animation="border" /></div>
                    ) : null}
                    {!signerPreviewModal.loading && signerPreviewModal.error ? (
                        <Alert variant="danger">{signerPreviewModal.error}</Alert>
                    ) : null}
                    {!signerPreviewModal.loading && !signerPreviewModal.error ? (
                        <>
                            <div className="mb-3">
                                <div className="fw-semibold">{recipientName(signerPreviewModal.recipient)}</div>
                                <div className="text-muted small">
                                    {signerPreviewModal.fields.length} field{signerPreviewModal.fields.length === 1 ? "" : "s"} across {signerPreviewModal.documents.length} document{signerPreviewModal.documents.length === 1 ? "" : "s"}
                                </div>
                            </div>
                            <Table size="sm" responsive>
                                <thead>
                                    <tr>
                                        <th>Field</th>
                                        <th>Document</th>
                                        <th>Page</th>
                                        <th className="text-end">Required</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {signerPreviewModal.fields.map((field) => {
                                        const previewDocument = signerPreviewModal.documents.find((document) => document.uuid === field.documentUuid);
                                        return (
                                            <tr key={field.uuid}>
                                                <td>
                                                    <div className="fw-semibold">{field.label || fieldTypeLabel(field.fieldType)}</div>
                                                    <div className="small text-muted">{fieldTypeLabel(field.fieldType)}</div>
                                                </td>
                                                <td>{documentTitle(previewDocument)}</td>
                                                <td>{field.pageNumber}</td>
                                                <td className="text-end">{field.required === false ? "No" : "Yes"}</td>
                                            </tr>
                                        );
                                    })}
                                    {signerPreviewModal.fields.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="text-muted">No fields assigned to this signer yet.</td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </Table>
                        </>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={closeSignerPreviewModal}>Close</Button>
                </Modal.Footer>
            </Modal>
        </Col>
    );
}

export default SignatureEnvelopeDetail;
